import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * The platform panel's own vocabulary.
 *
 * Deliberately not shared with the seller panel. The two applications say
 * different things to different people — "account" here means a customer, and
 * there it means the company you work for — and a shared dictionary would end
 * up with one of them saying the wrong one.
 *
 * English is the contract and Turkish is a translation of it, exactly as in the
 * seller panel: the typed shape comes from the English source, so a missing
 * translation is a compile error rather than a blank label.
 */

const en = {
    loading: 'Loading…',
    genericError: 'Something went wrong. Please try again.',
    signOut: 'Sign Out',
    // The tag under the logo, on the sign-in screen and atop the menu: what tells
    // this panel from the seller panel in an adjacent tab (docs/11 §8).
    panelName: 'Admin Panel',

    // What a failed request reads as (ADR 0005). The API names a refusal by a
    // code and these are its words; a code missing here reads as its status.
    errors: {
        status: {
            401: 'Your session has ended. Sign in again.',
            403: 'You do not have permission to do this.',
            404: 'That record was not found. Refresh the page and try again.',
            409: 'That conflicts with an existing record. Check the details and try again.',
            422: 'Some details were not accepted. Check them and try again.',
            429: 'Too many requests. Wait a moment and try again.',
        },
        problems: {
            'auth.signed_out': 'Your session has ended. Sign in again.',
            'auth.no_company': 'Choose a company first.',
            'auth.forbidden': 'You do not have permission to do this.',
            'auth.not_in_plan': "That company's package does not include this.",
            'auth.invalid_credentials': 'Email or password is incorrect.',
            'auth.session_expired': 'Your session has ended. Sign in again.',
            'auth.second_factor_invalid': 'That code is not right. Enter the code your authenticator app shows now, or an unused recovery code.',
            'auth.second_factor_expired': 'This sign-in has expired. Sign in again with your password.',
            'auth.two_factor_enabled': 'Two-step verification is already on.',
            'auth.two_factor_not_started': 'Start setting up two-step verification again.',
            'admin.already_impersonating': 'You are already acting inside an account. Leave it before entering another.',
            'admin.no_member_to_act_as': 'That account has no active member to act as.',
            'admin.staff_user_not_found': 'No account uses that e-mail address. They have to sign up first.',
            'admin.cannot_revoke_self': 'You cannot remove your own staff access.',
            'admin.status_not_allowed': 'That status cannot be set here.',
            'admin.account_erased': 'This account’s personal data has been erased; it cannot be reopened.',
            'admin.own_status': 'You cannot change your own status.',
            'admin.plan_key_taken': 'A package with that key already exists.',
            'admin.not_trialing': 'Only a running trial can be extended. Refresh the page.',
            'admin.reason_required': 'Write the reason; the audit log keeps it.',
            'admin.trial_days': 'Extend a trial by 1 to 90 days.',
            'admin.default_not_offered': 'The package new accounts start on must be on sale. Tick On Sale to New Accounts as well.',
            'admin.default_required': 'One package is always where new accounts start. Make another package the default first.',
            'admin.price_basis_unknown': 'Choose whether the prices include VAT.',
            'admin.subscription_not_lapsed':
                'Awaiting Payment and Expired are for a period that has ended: the period end must be a past date and the trial end must not be ahead. To open the account until a later date, choose Active.',
            'admin.subscription_lapsed':
                'Trialing and Active are for a period that has not ended: set a period end that is ahead, or leave it empty. Otherwise the account moves to Awaiting Payment within the hour.',
            'admin.cancel_passed':
                'A running subscription ends on its cancellation date: choose a date that is ahead, or leave it empty. Otherwise the account moves to Expired within the hour.',
            'admin.feature_level': 'That feature does not offer that level.',
            'auth.subscription_expired':
                "That company's subscription has expired: its orders, invoices and stock keep working, and nothing new is added until it is paid.",
            'auth.terms_not_accepted':
                'The current Terms of Use have not been accepted in this session. The seller accepts them in the seller panel.',
            'admin.charge_not_pending': 'That payment is no longer waiting. Refresh the list.',
            'admin.charge_held':
                'The payment is recorded as received but was not applied: the account has a change of package waiting. It is listed as On Hold; apply it once the change has taken effect, or refund it.',
            'admin.charge_not_paid': 'Only a paid or held payment can be refunded.',
            'admin.charge_not_held': 'That payment is no longer on hold. Refresh the list.',
            'admin.change_waiting':
                'The account still has a change of package waiting. Apply the payment once that change has taken effect or the package is set on the account’s page, or refund it.',
            'admin.bank_details_incomplete': 'Enter the account holder, the bank and the IBAN together, or clear all three.',
            'admin.iban_invalid': "That IBAN is not valid. Check it against the bank's own record.",
            'admin.charge_by_card': 'A card payment is settled by its payment service; it is not approved by hand.',
            'admin.refund_refused': (quote: string) => `The payment service refused the refund: ${quote}`,
            'admin.payment_service_unavailable': 'The payment service cannot be reached right now. Try again in a few minutes.',
            'admin.payment_provider_unknown': 'This deployment cannot use that payment service.',
            'admin.payment_provider_not_ready': (quote: string) => quote,
            'admin.payment_credential_missing': 'Fill in every required field of the connection.',
            'admin.payment_setting_unknown': 'Choose one of the values the payment service offers.',
            'admin.payment_environment': 'The payment service has no such environment. Choose Test or Live as it offers them.',
            'admin.payment_already_connected': 'That payment service is already connected.',
            'admin.payment_not_connected': 'Check the connection first: only one whose last check passed can take cards.',
            'admin.issuer_incomplete': 'Enter the legal name, tax number, tax office and full address together, or clear them all.',
            'admin.issuer_tax_number': 'That tax number is not valid. Check it against the tax certificate.',
            'admin.issuer_mersis_invalid': 'A MERSİS number is sixteen digits. Check it against the trade registry record.',
            'admin.issuer_kep_invalid': 'That KEP address is not an address. Write the whole address you were given.',
            'admin.invoicing_off':
                'No invoice can be issued yet. Choose a checked e-invoice service and complete the issuer details on Invoice Settings.',
            'admin.invoice_not_open': 'That payment has no invoice waiting. Refresh the list.',
            'admin.invoice_provider_unknown': 'This deployment cannot use that e-invoice service.',
            'admin.invoice_provider_not_ready': (quote: string) => quote,
            'admin.invoice_credential_missing': 'Fill in every required field of the connection.',
            'admin.invoice_setting_unknown': 'Choose one of the values the e-invoice service offers.',
            'admin.invoice_already_connected': 'That e-invoice service is already connected.',
            'admin.invoice_not_connected': 'Check the connection first: only one whose last check passed can issue invoices.',
        },
    },

    nav: {
        menu: 'Menu',
        overview: 'Overview',
        accounts: 'Accounts',
        users: 'People',
        requests: 'Requests',
        audit: 'Audit Log',
        system: 'System',
        plans: 'Packages',
        revenue: 'Revenue',
        payments: 'Payments',
        paymentSettings: 'Payment Settings',
        invoiceSettings: 'Invoice Settings',
        announcements: 'Announcements',
        staff: 'Staff',
        account: 'My Account',
    },

    navGroups: {
        sellers: 'Sellers',
        subscriptions: 'Subscriptions',
        administration: 'Administration',
    },

    common: {
        datePlaceholder: 'dd/mm/yyyy',
        pickDate: 'Pick From the Calendar',
        confirm: 'Confirm',
        cancel: 'Cancel',
        close: 'Close',
        all: 'All',
        never: 'Never',
        notFound: {
            title: 'Page Not Found',
            body: 'The address may be mistyped, or the page may have moved.',
            home: 'Go to the Home Page',
        },
        forbidden: {
            title: 'You Do Not Have Access to This Page',
            body: 'Your staff level does not include it. A superadmin can change your level.',
            home: 'Go to the Overview',
        },
        // A page that failed to draw (docs/11 §8). It words the failure and never the error.
        error: {
            title: 'The Page Could Not Be Shown',
            body: 'Something unexpected went wrong. Try again; if it keeps happening, reload the page.',
            retry: 'Try Again',
            home: 'Go to the Home Page',
        },
        no: 'No',
        save: 'Save',
        previous: 'Previous',
        next: 'Next',
        pageOf: (from: number, to: number, total: number) => `${from}–${to} of ${total}`,
        language: 'Language',
        theme: { label: 'Theme', light: 'Light', dark: 'Dark', system: 'System' },
    },

    auth: {
        title: 'Platform Sign-In',
        subtitle: 'For Entegrepazar staff. Sellers sign in on the seller panel.',
        email: 'E-mail',
        password: 'Password',
        submit: 'Sign in',
        invalid: 'E-mail or password is incorrect.',
        // A seller who reaches this application and signs in correctly is still not
        // staff. Said plainly rather than pretending the credentials were wrong,
        // because they were not, and a support call about it would waste both
        // sides' time.
        notStaff: 'This account is not platform staff.',
        secondFactor: {
            title: 'Two-Step Verification',
            hint: 'Enter the code your authenticator app shows. If you do not have your phone, enter one of your recovery codes.',
            code: 'Verification Code',
            submit: 'Verify',
            startOver: 'Sign In Again',
        },
    },

    account: {
        title: 'My Account',
        subtitle: 'How you sign in to the platform panel.',
        twoFactor: {
            title: 'Two-Step Verification',
            hint: 'Signing in asks for a code from an authenticator app on your phone as well as your password.',
            on: 'On',
            off: 'Off',
            enable: 'Turn On',
            disable: 'Turn Off',
            setupTitle: 'Turn On Two-Step Verification',
            scan: 'Scan this code with an authenticator app such as Google Authenticator or Microsoft Authenticator, then enter the code it shows.',
            qrLabel: 'QR code for the authenticator app',
            setupKey: 'Setup Key',
            setupKeyHint: 'If you cannot scan the code, enter this key in the app.',
            code: 'Verification Code',
            confirm: 'Turn On',
            recoveryTitle: 'Your Recovery Codes',
            recoveryHint: 'Each code signs you in once if you lose your phone. Keep them somewhere safe: this is the only time they are shown.',
            done: 'I Saved Them',
            disableTitle: 'Turn Off Two-Step Verification',
            disableHint: 'Enter your password and a code from your authenticator app, or a recovery code.',
            password: 'Password',
            disableConfirm: 'Turn Off',
            copy: { copy: 'Copy', copied: 'Copied' },
        },
    },

    overview: {
        title: 'Overview',
        subtitle: 'Every account, and what is not working inside them.',
        accounts: 'Accounts',
        activeAccounts: 'Active',
        users: 'Users',
        products: 'Products',
        orders: 'Orders',
        failingChannels: 'Failing Connections',
        failing: 'Connections That Have Stopped Working',
        failingEmpty: 'Every Connection Is Working',
        failingHint: 'The quietest failure this platform has: nothing breaks visibly, orders simply stop arriving.',
        overLimit: 'Accounts Above a Limit',
        overLimitHint:
            'Usage above what the package includes. Nothing is deleted; they cannot add more until they move to a larger package or use less.',
        overLimitEmpty: 'No Account Is Above a Limit',
        lapsed: 'Accounts With an Expired Subscription',
        lapsedHint:
            'Their orders, invoices and stock keep working; nothing new can be added. From the seventh day after the period ended, call them.',
        lapsedEmpty: 'No Subscription Has Expired',
        columns: {
            package: 'Package',
            limit: 'Limit',
            usage: 'Usage',
            contact: 'Contact',
            periodEnd: 'Period Ended',
            since: 'Since Then',
        },
        daysSince: (days: number) => (days === 1 ? '1 day' : `${days} days`),
        callNow: 'Time to Call',
    },

    accounts: {
        title: 'Accounts',
        subtitle: 'Every seller on the platform.',
        search: 'Search by name',
        empty: 'No Account Matched That Search',
        columns: {
            account: 'Account',
            status: 'Status',
            plan: 'Package',
            owner: 'Owner',
            products: 'Products',
            orders: 'Orders',
            channels: 'Channels',
            detail: 'Last Response',
        },
        status: {
            trial: 'Trial',
            active: 'Active',
            suspended: 'Suspended',
            cancelled: 'Closed',
        },
        subscription: {
            trialing: 'Trialing',
            active: 'Active',
            past_due: 'Awaiting Payment',
            canceled: 'Canceled',
            expired: 'Expired',
        },
        filterStatus: 'Status',
        noPlan: 'No Package',
        trialEnds: (date: string) => `ends ${date}`,
        enter: 'Enter Account',
        manage: 'Manage',
        // Confirmed, and the sentence says what will actually happen rather than
        // "are you sure". Support access is the most dangerous thing this panel can
        // do.
        confirmEnter: {
            title: 'Enter This Account?',
            body: 'You will see their data, the seller is shown a banner, and every action is recorded against you.',
            action: 'Open Support Session',
        },
        handoffFailed: 'The support session could not be opened.',
    },

    revenue: {
        title: 'Revenue',
        subtitle: 'What the platform earns, and who is leaving.',
        mrr: 'Monthly Recurring Revenue',
        mrrHint: 'Sum of the packages currently being paid for.',
        churn: 'Churned (30 Days)',
        churnHint: 'Subscriptions that ended in the last 30 days.',
        active: 'Active',
        trialing: 'Trialing',
        pastDue: 'Awaiting Payment',
        canceled: 'Canceled',
        expired: 'Expired',
        mix: 'Subscription Mix',
        distribution: 'Packages',
        columns: {
            plan: 'Package',
            price: 'Price / Month',
            subscribers: 'Subscribers',
            active: 'Active',
        },
    },

    announcements: {
        title: 'Announcements',
        subtitle: 'Broadcast to every seller. Each sees it as a notification in their inbox.',
        send: 'Send a Broadcast',
        titleField: 'Title',
        body: 'Message',
        level: 'Level',
        submit: 'Send to Everyone',
        history: 'History',
        empty: 'No Broadcast Has Been Sent Yet',
        audience: 'Delivered to',
        when: 'Sent',
        levels: {
            info: 'Info',
            success: 'Success',
            warning: 'Warning',
            error: 'Error',
        },
    },

    // The features a plan includes and their levels (ADR 0015), by the keys
    // the code holds.
    feature: {
        names: {
            storefront: 'Tezgah',
            invoicing: 'e-Invoicing',
            price_rules: 'Pricing',
            suppliers: 'Suppliers',
            integrations: 'API Access and Webhooks',
            finance: 'Finance',
        },
        levels: {
            off: 'Off',
            read: 'Read Only',
            full: 'Full Access',
        },
    },

    // The four limits a package sets (ADR 0017 §1), by the resource the API names.
    limits: {
        products: 'Products',
        channels: 'Marketplace Accounts',
        users: 'Users',
        orders_monthly: 'Orders This Month',
    },

    accountDetail: {
        back: 'Back to Accounts',
        notFound: 'No such account.',
        tabs: {
            overview: 'Overview',
            people: 'People',
            channels: 'Channels',
            access: 'Access',
            audit: 'Audit',
        },
        since: (date: string) => `Since ${date}`,
        suspend: 'Suspend',
        reactivate: 'Reopen',
        confirmSuspend: {
            title: 'Suspend This Account?',
            body: 'Everyone inside it is signed out at once and cannot sign back in. Nothing is deleted; reopening restores access.',
            action: 'Suspend',
        },
        confirmReactivate: {
            title: 'Reopen This Account?',
            body: 'Its members can sign in again immediately.',
            action: 'Reopen',
        },
        close: 'Close Account',
        confirmClose: {
            title: 'Account Will Be Closed',
            body: 'Everyone inside it is signed out and cannot sign back in. A year after closing, its personal data is erased for good: people, customers, connections and contact details. Invoices and accounting records are kept. Until then the account can be reopened.',
            action: 'Close Account',
        },
        closedOn: (date: string) => `Closed on ${date}. Its personal data is erased a year after closing; until then it can be reopened.`,
        erasedOn: (date: string) => `Its personal data was erased on ${date}. Invoices and accounting records are kept.`,
        statusChanged: 'Account status updated.',
        summary: 'Summary',
        owner: 'Owner',
        noOwner: 'No active owner',
        legalName: 'Legal Name',
        taxNumber: 'Tax Number',
        taxOffice: 'Tax Office',
        counts: {
            users: 'People',
            products: 'Products',
            orders: 'Orders',
            channels: 'Channels',
        },
        lastOrder: 'Last Order',
        shop: 'Storefront',
        shopPublished: 'Published',
        shopDraft: 'Not published',
        noShop: 'No storefront',
        subscription: 'Subscription',
        subscriptionStatus: 'Status',
        trialEnds: 'Trial Ends',
        periodEnd: 'Current Period Ends',
        cancelAt: 'Cancels on',
        people: 'People',
        peopleEmpty: 'Nobody Is in This Account',
        peopleColumns: {
            person: 'Person',
            role: 'Role',
            status: 'Status',
            verified: 'E-mail',
            lastLogin: 'Last Sign-In',
        },
        verified: 'Verified',
        unverified: 'Unverified',
        memberStatus: { invited: 'Invited', active: 'Active', disabled: 'Removed' },
        // Who accepted which version of the terms, at sign-up and after (ADR 0018 §1).
        terms: 'Terms of Use Acceptances',
        termsHint: 'Recorded at sign-up and whenever someone accepts a new version. Read only.',
        termsEmpty: 'No Acceptance Recorded',
        termsColumns: {
            person: 'Person',
            terms: 'Terms of Use',
            notice: 'Privacy Notice',
            acceptedAt: 'Accepted At',
            client: 'IP Address and Browser',
        },
        version: (date: string) => `${date} version`,
        channels: 'Connections',
        channelsEmpty: 'No Marketplace Connection',
        channelColumns: {
            channel: 'Channel',
            status: 'Status',
            verified: 'Verified',
            detail: 'Last Response',
        },
        channelStatus: {
            pending: 'Pending',
            connected: 'Working',
            failed: 'Failed',
            disabled: 'Disabled',
        },
        sandbox: 'Test Environment',
        // A stored failure class, written out; the seller panel says the same
        // (docs/15 §6) without telling staff to try again.
        channelProblem: {
            auth: 'Store details were not accepted.',
            validation: 'The marketplace did not accept what was sent.',
            conflict: 'The record changed on the marketplace meanwhile.',
            not_found: 'The marketplace could not find the record.',
            deprecated: 'The marketplace no longer supports this call.',
            rate_limited: 'The marketplace received too many requests.',
            transient: 'The marketplace could not be reached.',
            channel_down: 'The marketplace could not be reached.',
            unknown: 'An unexpected problem occurred.',
        },
        handoffs: 'Support Sessions',
        handoffsHint: 'Every time staff entered this account, who and when.',
        handoffsEmpty: 'Nobody From the Platform Has Entered This Account',
        handoffColumns: {
            staff: 'Staff',
            actedAs: 'Acted as',
            when: 'When',
            used: 'Used',
        },
        unused: 'Not used',
        audit: 'Audit Log',
        auditHint: 'What happened inside this account, newest first.',
        plan: 'Package',
        planHint: 'Move this account onto a package at once. This is the path a customer cannot take themselves.',
        planDates: {
            trialEndsAt: 'Trial Ends',
            periodEnd: 'Period Ends',
            cancelAt: 'Cancels on',
            hint: 'Leave a date empty to clear it.',
        },
        planKey: 'Package',
        status: 'Status',
        apply: 'Apply Package',
        planApplied: 'Package updated.',
        fitWithin: 'Usage is within this package, and no feature closes.',
        fitExceeds: 'Usage would be above these limits:',
        fitLoses: 'These features would close or narrow:',
        usage: 'Package Usage',
        usageHint: 'Counted as billing counts it; a limit the package leaves empty is not shown.',
        usageNone: 'This package sets no limit.',
        overLimit: 'Above the limit',
        scheduled: (date: string, plan: string) => `On ${date} the account moves to the ${plan} package.`,
        extendTrial: 'Extend Trial',
        extendTitle: 'Extend the Trial',
        extendBody: (date: string) => `The trial now ends on ${date}. The account is told, and the audit log keeps the reason.`,
        extendDays: 'Days',
        extendDaysHint: 'From 1 to 90 days, added to the current end.',
        extendReason: 'Reason',
        extendAction: 'Extend Trial',
        extended: (date: string) => `Trial extended to ${date}.`,
        features: 'Feature Levels',
        featuresHint:
            "What each part of the product is for this account: its package's level, or one set here for this account alone. A customer cannot change it.",
        featureColumns: {
            feature: 'Feature',
            plan: 'In the Package',
            own: 'For This Account',
            effective: 'Applies',
        },
        change: 'Change',
        levelTitle: (feature: string) => `${feature} for This Account`,
        level: 'Level',
        asPlan: (level: string) => `As the Package Says (${level})`,
        until: 'Until',
        untilHint: "Empty for no end. After it, the package's level applies again.",
        lapsed: 'Lapsed',
        note: 'Note (Optional)',
        save: 'Save Level',
    },

    staff: {
        title: 'Staff',
        subtitle: 'Who works on the platform, and what they may do.',
        empty: 'No Platform Staff Yet',
        columns: {
            level: 'Level',
        },
        level: {
            support: 'Support',
            admin: 'Admin',
            superadmin: 'Superadmin',
        },
        levelHint: {
            support: 'Can read the platform views.',
            admin: 'Can also enter a customer account.',
            superadmin: 'Can also decide who is staff.',
        },
        add: 'Add Staff',
        email: 'E-mail of an Existing Account',
        emailHint: 'They have to have signed up first. Granting access does not create an account.',
        grant: 'Grant',
        revoke: 'Remove',
        confirmRevoke: {
            title: 'Remove Platform Access?',
            body: 'Their sessions end immediately.',
            action: 'Remove',
        },
    },

    users: {
        title: 'People',
        subtitle: 'Everyone with a sign-in, across every account.',
        search: 'Search by name or e-mail',
        empty: 'Nobody Matched That Search',
        columns: {
            person: 'Person',
            accounts: 'Accounts',
            status: 'Status',
            verified: 'E-mail',
            lastLogin: 'Last Sign-In',
            created: 'Signed up',
        },
        status: { active: 'Active', disabled: 'Disabled' },
        staff: 'Staff',
        disable: 'Disable',
        enable: 'Enable',
        confirmDisable: {
            title: 'Disable This Person?',
            body: 'They are signed out everywhere at once and cannot sign in until re-enabled. Their accounts and data stay.',
            action: 'Disable',
        },
        confirmEnable: {
            title: 'Re-enable This Person?',
            body: 'They can sign in again immediately.',
            action: 'Enable',
        },
        resetTwoFactor: 'Reset Two-Step Verification',
        confirmResetTwoFactor: {
            title: 'Two-Step Verification Will Be Reset',
            body: 'Their authenticator and recovery codes stop working and they are signed out everywhere. They sign in with their password alone and can set it up again.',
            action: 'Reset',
        },
    },

    audit: {
        title: 'Audit Log',
        subtitle: 'Who did what, across the platform. Newest first.',
        search: 'Search by action, actor or entity',
        empty: 'Nothing Matched',
        columns: {
            when: 'When',
            actor: 'Who',
            action: 'What',
            entity: 'On',
            changes: 'What Changed',
            account: 'Account',
            ip: 'From',
        },
        actorType: {
            user: 'User',
            system: 'System',
            api_token: 'API Token',
            platform_admin: 'Platform Staff',
        },
        // Who was really there when a row was written during support access.
        viaSupport: (staff: string) => `Support: ${staff}`,
        // The actions the log records, written out. One missing here shows its
        // code rather than a blank.
        actions: {
            'subscription.changed': 'Subscription changed',
            'plan.created': 'Package created',
            'plan.updated': 'Package updated',
            'plan.availability_changed': "Package's sale changed",
            'subscription.trial_extended': 'Trial extended',
            'subscription.cancelled': 'Subscription cancelled',
            'subscription.resumed': "Subscription's cancellation taken back",
            'billing_settings.vat_basis_changed': 'VAT basis of prices changed',
            'tenant.registered': 'Account created',
            'tenant.updated': 'Account details updated',
            'tenant.suspended': 'Account suspended',
            'tenant.reactivated': 'Account reopened',
            'tenant.closed': 'Account closed',
            'tenant_feature.changed': 'Feature level changed',
            'tenant_feature.cleared': 'Feature level handed back to the package',
            'plan_feature.changed': "A package's feature level changed",
            'user.disabled': 'Person disabled',
            'user.enabled': 'Person re-enabled',
            'user.profile_updated': 'Profile updated',
            'user.password_changed': 'Password changed',
            'user.password_reset': 'Password reset',
            'user.email_changed': 'E-mail changed',
            'terms.accepted': 'Terms of Use accepted',
            'auth.signed_in': 'Signed in',
            'auth.signed_out': 'Signed out',
            'auth.email_verified': 'E-mail verified',
            'auth.support_handoff_issued': 'Support session opened',
            'auth.impersonation_started': 'Account entered for support',
            'team.invited': 'Invited to the team',
            'team.invitation_revoked': 'Invitation withdrawn',
            'team.invitation_accepted': 'Invitation accepted',
            'team.member_updated': 'Team member updated',
            'team.role_saved': 'Role saved',
            'team.role_deleted': 'Role deleted',
            'platform.staff_granted': 'Staff access granted',
            'platform.staff_revoked': 'Staff access removed',
            'request.updated': 'Request updated',
            'announcement.created': 'Announcement published',
            'billing_charge.paid': 'Payment received',
            'billing_charge.held': 'Payment received, not applied',
            'billing_charge.applied': 'Payment applied',
            'billing_charge.cancelled': 'Payment cancelled',
            'billing_charge.refunded': 'Payment refunded',
            'billing_charge.invoiced': "Payment's invoice issued",
            'billing_charge.invoice_failed': "Payment's invoice could not be issued",
            'billing_charge.invoice_recorded': 'Invoice issued elsewhere recorded',
            'billing_settings.changed': 'Bank transfer details changed',
            'billing_issuer.changed': 'Issuer details changed',
            'billing_charge.failed': 'Card payment refused',
            'platform_payment_account.connected': 'Payment service connected',
            'platform_payment_account.used': 'Payment service chosen for cards',
            'platform_payment_account.unused': 'Card payments stopped',
            'platform_payment_account.withdrawn': 'Payment service withdrawn',
            'platform_invoice_account.connected': "Platform's e-invoice service connected",
            'platform_invoice_account.used': 'e-Invoice service chosen for invoices',
            'platform_invoice_account.unused': 'Invoices stopped',
            'platform_invoice_account.withdrawn': "Platform's e-invoice service withdrawn",
            'invoice_provider.opened': 'e-Invoice service offered to sellers',
            'invoice_provider.closed': 'e-Invoice service hidden from sellers',
            'auth.sign_in_failed': 'Sign-in failed',
            'auth.recovery_code_used': 'Signed in with a recovery code',
            'auth.two_factor_enabled': 'Two-step verification turned on',
            'auth.two_factor_disabled': 'Two-step verification turned off',
            'platform.two_factor_reset': 'Two-step verification reset',
            'api_token.created': 'API key created',
            'api_token.revoked': 'API key revoked',
            'webhook_endpoint.created': 'Notification address added',
            'webhook_endpoint.deleted': 'Notification address removed',
            'channel_account.connected': 'Sales channel connected',
            'channel_account.credentials_changed': "Sales channel's credentials changed",
            'invoice_account.connected': 'e-Invoice service connected',
            'invoice_account.credentials_changed': "e-Invoice service's credentials changed",
            'carrier_account.connected': 'Carrier integration connected',
            'payment_account.connected': "Shop's payment service connected",
            'payment_account.credentials_changed': "Shop's payment service keys changed",
            'sms_account.connected': 'SMS service connected',
            'sms_account.updated': 'SMS service edited',
            'feed.password_changed': "Supplier list's password changed",
            'feed.password_removed': "Supplier list's password removed",
            'listing.bulk_applied': 'Prices and stock edited in bulk',
            'listing.match_approved': "A channel's product match approved",
            'listing.match_rejected': "A channel's product match rejected",
            'listing.price_lock_released': "A channel's price lock released",
            'inventory.bulk_adjusted': 'Stock edited in bulk',
            'inventory.warehouse_transferred': "A warehouse's stock moved to another",
            'inventory.warehouse_paused': 'Warehouse paused: its stock taken off the channels',
            'inventory.warehouse_resumed': 'Warehouse resumed: its stock offered on the channels again',
            'inventory.stock_transferred': "Part of a variant's stock moved to another warehouse",
            'product.bulk_status_changed': "Products' status changed in bulk",
            'product.bulk_category_changed': "Products' category changed in bulk",
            'product.bulk_reverted': 'Bulk edit undone',
            'product.bulk_deleted': 'Products deleted in bulk',
            'brand.updated': 'Brand edited',
            'brand.merged': 'Brands merged',
            'brand.deleted': 'Brand deleted',
            'order.status_requested': 'Order status sent to the channel',
            'order.cancellation_requested': 'Order cancellation sent to the channel',
            'order.split_requested': 'Package split sent to the channel',
            'order.carrier_change_requested': 'Carrier change sent to the channel',
            'order.bulk_status_requested': 'Status sent for several orders',
            'claim.opened': 'Return or cancellation recorded',
            'claim.decided': 'Return or cancellation answered',
            'claim.pre_approved': 'Pre-approval given on a marketplace request',
            'channel_account.removed': 'Sales channel removed',
            'invoice_account.removed': 'e-Invoice service removed',
            'carrier_account.removed': 'Carrier integration removed',
            'payment_account.removed': "Shop's payment service removed",
            'sms_account.disconnected': 'SMS service removed',
            'product.updated': 'Product edited',
            'product.prices_changed': "Product's prices changed",
            'product.imported': 'Products imported from a file',
            'inventory.adjusted': 'Stock edited',
            'listing.offer_changed': "A product's price and stock on a channel edited",
            'listing.rows_edited': 'Price and stock sheet edited',
            'listing.sheet_imported': 'Prices and stock edited from Excel',
            'shipment.booked': 'Shipment booked',
            'invoice.issued': 'Invoice issued',
            'invoice.bulk_issued': 'Invoices issued in bulk',
            'order.payment_received': 'Bank transfer marked received',
        },
        entities: {
            subscription: 'Subscription',
            plan: 'Package',
            tenant: 'Account',
            tenant_feature: 'Feature',
            user: 'Person',
            session: 'Session',
            invitation: 'Invitation',
            role: 'Role',
            platform_request: 'Request',
            announcement: 'Announcement',
            billing_charge: 'Payment',
            billing_settings: 'Payment Settings',
            platform_payment_account: 'Payment Service',
            platform_invoice_account: "Platform's e-Invoice Service",
            api_token: 'API Key',
            webhook_endpoint: 'Notification Address',
            channel_account: 'Sales Channel',
            invoice_account: 'e-Invoice Service',
            invoice_provider: 'e-Invoice Service',
            carrier_account: 'Carrier Integration',
            payment_account: "Shop's Payment Service",
            sms_account: 'SMS Service',
            feed: 'Supplier List',
            warehouse: 'Warehouse',
            bulk_edit: 'Bulk Edit',
            product: 'Product',
            variant: 'Variant',
            brand: 'Brand',
            order: 'Order',
            claim: 'Return or Cancellation',
            stock: 'Stock',
            invoice: 'Invoice',
        },
        // The fields a change is shown by, in this order. A field with no word
        // here is left out of the list rather than shown by its column name.
        fields: {
            plan_key: 'Package',
            status: 'Status',
            trial_ends_at: 'Trial Ends',
            current_period_end: 'Period Ends',
            cancel_at: 'Cancels on',
            name: 'Name',
            description: 'Description',
            monthly_price: 'Monthly Price',
            yearly_price: 'Yearly Price',
            max_products: 'Product Limit',
            max_channels: 'Channel Limit',
            max_users: 'User Limit',
            max_orders_monthly: 'Monthly Order Limit',
            trial_days: 'Trial Days',
            is_active: 'On Sale',
            is_offered: 'On Sale to New Accounts',
            is_renewable: 'Renewable',
            is_default: 'Where New Accounts Start',
            days: 'Days',
            reason: 'Reason',
            price_vat_basis: 'VAT in Prices',
            sort: 'Order',
            features: 'Includes',
            highlight: 'Recommended Package',
            custom_pricing: 'Custom Pricing',
            feature: 'Feature',
            enabled: 'On',
            expires_at: 'Expires',
            note: 'Note',
            title: 'Title',
            level: 'Level',
            audience: 'Recipients',
            reference: 'Reference',
            period: 'Period',
            amount: 'Amount',
            method: 'Paid by',
            period_end: 'Period Ends',
            bank_holder: 'Account Holder',
            bank_name: 'Bank',
            iban: 'IBAN',
            provider: 'Service',
            environment: 'Environment',
            failure_reason: 'Reason',
            // A credential is recorded by the names of its fields, never
            // their values.
            fields: 'Changed Fields',
            count: 'Records',
            target: 'Moved To',
            quantity: 'Units',
            url: 'Address',
            prefix: 'Key Begins With',
            step: 'Failed At',
            failures: 'Failed Attempts in a Row',
            locked: 'Account Locked',
            channel: 'Channel',
            sku: 'Stock Code',
            prices: 'Prices',
            offers: 'Price and Stock',
            kind: 'Type',
            format: 'File Type',
            existing: 'Already in the Catalogue',
            over_limit: 'Over the Package Limit',
            failed: 'Not Added',
        },
        values: {
            yes: 'Yes',
            no: 'No',
        },
        // An invoice's type, as an issued one records it.
        kinds: {
            einvoice: 'e-Invoice',
            earchive: 'e-Archive',
            credit_note: 'Credit Note',
        },
        // The step a failed sign-in stopped at.
        steps: {
            password: 'Password',
            second_factor: 'Verification Code',
        },
        vatBases: {
            inclusive: 'Included',
            exclusive: 'Added',
        },
        statuses: {
            trialing: 'Trial',
            trial: 'Trial',
            active: 'Active',
            past_due: 'Awaiting Payment',
            canceled: 'Cancelled',
            cancelled: 'Cancelled',
            expired: 'Expired',
            suspended: 'Suspended',
            disabled: 'Disabled',
            open: 'Open',
            closed: 'Closed',
            approved: 'Approved',
            rejected: 'Rejected',
            resolved: 'Resolved',
        },
    },

    system: {
        title: 'System',
        subtitle: 'The machinery: queue, outbox, webhooks, feeds. Refreshes every 30 seconds.',
        queue: 'Job Queue',
        available: 'Waiting',
        running: 'Running',
        retryable: 'Retrying',
        scheduled: 'Scheduled',
        discarded: 'Given up (24h)',
        completed: 'Completed (24h)',
        outbox: 'Event Outbox',
        outboxPending: 'Unpublished',
        outboxStuck: 'Older Than 10 Min',
        outboxFailing: 'With Errors',
        delivery: 'Deliveries',
        webhooksPending: 'Webhooks Pending',
        webhooksFailed: 'Webhooks Failed (24h)',
        feedsRunning: 'Feeds Running',
        feedsFailed: 'Feeds Failed (24h)',
        access: 'Access',
        sessions: 'Live Sessions',
        handoffs: 'Open Support Links',
        rates: 'Exchange Rates Fetched',
        ratesStale: 'Rates are more than a day old.',
        byKind: 'Queue by Job Kind',
        byKindEmpty: 'The Queue Is Empty',
        kindColumns: { kind: 'Kind', state: 'State', count: 'Count' },
        failures: 'Recent Failures',
        failuresHint: 'The last things that went wrong, with what they said.',
        failuresEmpty: 'Nothing Has Failed Recently',
        failureColumns: {
            source: 'Where',
            reference: 'What',
            account: 'Account',
            error: 'Error',
            when: 'When',
        },
        source: { job: 'Job', webhook: 'Webhook', feed: 'Feed' },
        state: {
            available: 'Waiting',
            running: 'Running',
            retryable: 'Retrying',
            scheduled: 'Scheduled',
            pending: 'Pending',
            completed: 'Completed',
            discarded: 'Given up',
            cancelled: 'Cancelled',
        },
    },

    requests: {
        title: 'Requests',
        subtitle: 'What people wrote in: messages from the site and package-change requests from sellers.',
        empty: 'Nothing Is Waiting',
        filterStatus: 'Status',
        filterKind: 'Kind',
        status: { open: 'Open', closed: 'Closed' },
        kind: { contact: 'Message', plan_change: 'Package Change' },
        source: { www: 'Site', panel: 'Panel' },
        columns: {
            when: 'When',
            kind: 'Kind',
            who: 'Who',
            account: 'Account',
            plan: 'Package',
            message: 'Message',
            status: 'Status',
        },
        open: 'Open',
        detail: 'Request',
        note: 'Note',
        noteHint: 'What was done, for whoever reads this next.',
        close: 'Close Request',
        reopen: 'Reopen',
        closedBy: (who: string, when: string) => `Closed by ${who} · ${when}`,
        closed: 'Request closed.',
        reply: 'Reply by E-mail',
    },

    plans: {
        title: 'Packages',
        subtitle: 'What each package costs, includes and allows. A change applies to every account on it.',
        columns: {
            plan: 'Package',
            price: 'Price / Month',
            yearlyPrice: 'Price / Year',
            limits: 'Limits',
            trial: 'Trial',
            subscribers: 'Accounts',
            status: 'Sale',
        },
        offered: 'On Sale',
        renewableOnly: 'Renewal Only',
        withdrawn: 'Not Sold',
        isDefault: 'Default',
        // Each limit that is set; an empty one is not listed (docs/15 §2).
        limitParts: {
            products: (count: string) => `${count} products`,
            channels: (count: string) => `${count} marketplace accounts`,
            users: (count: string) => `${count} users`,
            orders: (count: string) => `${count} orders a month`,
        },
        noLimits: 'No limits',
        trialDays: (days: number) => `${days} days`,
        edit: 'Edit',
        editTitle: (name: string) => `Edit Package: ${name}`,
        add: 'Add Package',
        addTitle: 'New Package',
        contactSales: 'Priced by offer',
        free: 'Free',
        notSoldYearly: 'Not sold yearly',
        highlighted: 'Recommended Package',
        // A package's price states its VAT (docs/15 §4).
        plusVat: (price: string) => `${price} + VAT`,
        withVat: (total: string) => `VAT included ${total}`,
        perMonth: '/ month',
        yearlyNote: (price: string) => `Yearly, paid upfront: ${price} + VAT`,
        preview: 'Preview',
        previewHint: 'The card as sellers and the site will show it, priced on the platform’s VAT basis.',
        includes: 'Includes',
        notIncluded: 'Not included',
        recommended: 'Recommended Package',
        fields: {
            key: 'Key (Lowercase, e.g. growth_plus)',
            name: 'Name',
            description: 'Description',
            price: 'Monthly Price (₺)',
            yearlyPrice: 'Yearly Price (₺)',
            priceHint: (basis: string) => `In lira. ${basis}`,
            yearlyPriceHint: 'Leave empty to sell the package monthly only.',
            basisExclusive: 'VAT is added on top of it.',
            basisInclusive: 'It includes VAT.',
            currency: 'Currency',
            maxProducts: 'Product Limit',
            maxChannels: 'Marketplace Account Limit',
            maxUsers: 'User Limit',
            maxOrders: 'Monthly Order Limit',
            trialDays: 'Trial Days',
            sort: 'Order',
            availability: 'Sale',
            isOffered: 'On Sale to New Accounts',
            isOfferedHint: 'Shown on the site and to accounts on another package.',
            isRenewable: 'Accounts on It May Renew',
            isRenewableHint: 'Off, an account on it is left at the end of its period.',
            isDefault: 'New Accounts Start Their Trial on It',
            isDefaultHint: 'Exactly one package; to change it, tick it on another package.',
            unlimitedHint: 'Leave empty for no limit; an empty limit is not shown.',
            features: 'Extra Items',
            featuresHint: 'One line per item, shown after what the package includes.',
            featureLevels: 'Feature Levels',
            featureLevelsHint: 'What an account on this package may use. An account can be given its own level on its page.',
            defaultLevel: 'Full Access (default)',
            highlight: 'Show as the Recommended Package',
            customPricing: 'No Price: Sold by Offer',
        },
        saved: 'Package updated.',
        created: 'Package added.',
        readOnly: 'Only a superadmin can change a package.',
    },

    payments: {
        title: 'Payments',
        subtitle: 'What sellers paid for their packages. A bank transfer waits here until it is approved.',
        filterStatus: 'Status',
        empty: 'No Payments',
        emptyPending: 'No Payment Is Waiting',
        status: { pending: 'Awaiting Approval', paid: 'Paid', held: 'On Hold', failed: 'Failed', cancelled: 'Cancelled', refunded: 'Refunded' },
        // Why a payment is held (ADR 0017 §8).
        heldReason: 'Not applied: a change of package was waiting when it arrived.',
        periods: { monthly: 'Monthly', yearly: 'Yearly' },
        methods: { bank_transfer: 'Bank Transfer', card: 'Card' },
        columns: {
            when: 'When',
            account: 'Account',
            plan: 'Package',
            amount: 'Amount',
            method: 'Paid by',
            reference: 'Reference',
            status: 'Status',
            invoice: 'Invoice',
            agreement: 'Contract',
        },
        // What a charge was bought on and its evidence (ADR 0018 §5).
        agreement: {
            view: 'View',
            viewLabel: (reference: string) => `View the contract of payment ${reference}`,
            title: 'Accepted Contract',
            text: 'Pre-Information Form and Distance Service Contract',
            reference: 'Reference',
            acceptedBy: 'Accepted By',
            acceptedAt: 'Accepted At',
            ip: 'IP Address',
            userAgent: 'Browser',
            version: 'Version',
            sha256: 'SHA-256',
            boxes: 'Confirmations',
            preInformation: 'Read and confirmed the pre-information form and the distance contract',
            commercialPurpose: 'Bought for a commercial or professional activity',
            immediateStart: 'Agreed to the service starting at once, without a right of withdrawal',
        },
        // A paid plan's invoice (ADR 0016 §9), in the glossary's words (docs/15 §2).
        invoiceStatus: { pending: 'Being Issued', waiting: 'Waiting', issued: 'Issued', failed: 'Not Issued' },
        invoiceError: {
            unavailable: 'The e-invoice service could not be reached.',
            company_incomplete: "The company's billing details are incomplete.",
            failed: 'The service refused it without giving a reason.',
        },
        invoiceActions: 'Invoice Actions',
        openInvoice: 'Open the Invoice',
        issueInvoice: 'Issue the Invoice',
        fetchInvoice: 'Ask for the Document Again',
        recordInvoice: 'Record an Invoice Issued Elsewhere',
        issueOpen: 'Issue Waiting Invoices',
        invoicingOff: 'Invoices are not being issued. Choose an e-invoice service and complete the issuer details first.',
        invoiceSettings: 'Invoice Settings',
        issuedOpen: (count: number) => (count === 0 ? 'No invoice is waiting.' : `${count} invoices are being issued.`),
        confirmRecordInvoice: {
            title: 'Invoice Will Be Recorded',
            body: (reference: string) => `Record the number of the invoice issued elsewhere for ${reference}. No invoice is then issued here for it.`,
            action: 'Record the Invoice',
        },
        invoiceNumber: 'Invoice Number',
        until: (date: string) => `Until ${date}`,
        startsOn: (date: string) => `Starts on ${date}`,
        approve: 'Approve',
        apply: 'Apply',
        cancel: 'Cancel',
        refund: 'Mark Refunded',
        refundCard: 'Refund',
        // A card waits on its service, never on staff.
        cardPending: 'Not Completed',
        note: 'Note (Optional)',
        confirmApprove: {
            title: 'Payment Will Be Approved',
            body: (account: string, plan: string, period: string, amount: string) =>
                `${account} buys the ${plan} package (${period}) for ${amount}. While a paid period is still running, the new package starts when it ends. Approve only once the transfer is in the account.`,
            action: 'Approve Payment',
        },
        confirmApply: {
            title: 'Payment Will Be Applied',
            body: (account: string, plan: string, period: string, amount: string) =>
                `${amount} that ${account} paid for the ${plan} package (${period}) is applied as an approved payment is: while a paid period is still running, a new package starts when it ends. Its invoice is then issued.`,
            action: 'Apply Payment',
        },
        confirmCancel: {
            title: 'Payment Will Be Cancelled',
            body: (reference: string) =>
                `${reference} closes and the seller is left on their package. A transfer that arrives after this is sent back.`,
            action: 'Cancel Payment',
        },
        confirmRefund: {
            title: 'Payment Will Be Marked Refunded',
            body: (reference: string, amount: string) =>
                `Record that ${amount} for ${reference} went back. The package stays as it is; change it on the account's page if it should.`,
            action: 'Mark Refunded',
        },
        confirmRefundCard: {
            title: 'Payment Will Be Refunded',
            body: (reference: string, amount: string) =>
                `${amount} for ${reference} goes back to the card through the payment service that took it. The package stays as it is; change it on the account's page if it should.`,
            action: 'Refund to the Card',
        },
        done: {
            approved: 'Payment approved. The package is applied to the account.',
            applied: 'Payment applied. The package is applied to the account.',
            cancelled: 'Payment cancelled.',
            refunded: 'Payment refunded.',
            invoiceQueued: 'The invoice is being issued.',
            invoiceRecorded: 'Invoice recorded.',
        },
    },

    paymentSettings: {
        title: 'Payment Settings',
        subtitle: 'How sellers pay for their packages, and whether the prices include VAT.',
        bankTransfer: 'Bank Transfer',
        bankTransferHint: 'The account sellers send a transfer to. They are shown it with the amount and the reference to write in the description.',
        notOffered: 'Until the account holder, bank and IBAN are all set, sellers are not offered a bank transfer.',
        readOnly: 'Only a superadmin can change where sellers pay.',
        holder: 'Account Holder',
        bank: 'Bank',
        iban: 'IBAN',
        ibanHint: 'Shown to sellers in groups of four. Clearing all three fields withdraws the bank transfer.',
        saved: 'Payment settings saved.',
        vat: {
            title: 'VAT in Package Prices',
            hint: 'Whether a price typed on Packages includes VAT. Every price shown and every payment started after a change follows it; payments already started keep their amounts.',
            readOnly: 'Only a superadmin can change it.',
            exclusive: 'VAT Is Added to the Price',
            exclusiveHint: 'A package priced ₺1.499,00 is sold for ₺1.499,00 + VAT, ₺1.798,80 with VAT.',
            inclusive: 'The Price Includes VAT',
            inclusiveHint: 'A package priced ₺1.798,80 is sold for ₺1.499,00 + VAT, ₺1.798,80 with VAT.',
            save: 'Save',
            saved: 'VAT basis saved.',
            confirm: {
                title: 'The VAT Basis of Prices Will Change',
                body: 'Every package price shown, and every payment started from now on, is worked out on the new basis. Payments already started keep their amounts.',
                action: 'Change the VAT Basis',
            },
        },
        card: {
            title: 'Card Payment',
            hint: 'The payment service sellers pay by card through, on its own page. Connect it, check it, then choose it.',
            notOffered: 'Until a checked payment service is chosen, sellers are not offered card payment.',
            empty: 'No payment service is connected.',
            callbackUrl: (name: string) => `${name}: Notification Address`,
            copy: { copy: 'Copy', copied: 'Copied' },
            connect: 'Connect a Payment Service',
            verify: 'Check the Connection',
            use: 'Use for Card Payment',
            stop: 'Stop Card Payment',
            withdraw: 'Remove the Connection',
            inUse: 'In Use',
            status: { pending: 'Not Checked', connected: 'Connected', failed: 'Connection Problem' },
            problem: { unavailable: 'The service could not be reached.', failed: 'The check did not pass.' },
            settingsHint: 'What the service lets you decide about how sellers pay.',
            back: 'Back to the Services',
            choose: 'Connect',
            connected: 'Connected',
            notReady: 'Not Ready',
            confirmStop: {
                title: 'Card Payment Will Stop',
                body: 'Sellers are no longer offered card payment. Payments already begun are still settled by the service.',
                action: 'Stop Card Payment',
            },
            confirmWithdraw: {
                title: 'The Connection Will Be Removed',
                body: 'It takes no new card. Its keys are kept, so payments it took are still settled and refunded through it.',
                action: 'Remove the Connection',
            },
            done: {
                connected: 'Payment service connected. Check it before choosing it.',
                chosen: 'Sellers now pay by card through this service.',
                stopped: 'Card payment stopped.',
                withdrawn: 'Connection removed.',
            },
        },
    },

    invoiceSettings: {
        title: 'Invoice Settings',
        subtitle: 'The invoice every paid package gets: the service it is issued through and the name it is issued in.',
        notReady:
            'Invoices are not being issued yet. Paid packages wait until a checked e-invoice service is chosen and the issuer details are complete; then issue them on Payments.',
        issuer: {
            title: 'Issuer',
            hint: "The platform's legal identity, as every invoice and every seller's distance contract carries it.",
            mersisNo: 'MERSİS No',
            mersisHint: 'Sixteen digits. Printed on the distance contract; left off it while empty.',
            kepAddress: 'KEP Address',
            kepHint: 'Printed on the distance contract as an address for notices; left off it while empty.',
            name: 'Legal Name',
            taxNumber: 'Tax Number',
            taxOffice: 'Tax Office',
            address: 'Address',
            city: 'Province',
            district: 'District',
            choose: 'Choose',
            phone: 'Phone',
            email: 'E-mail',
            incomplete: 'Until the legal name, tax number, tax office and full address are set, no invoice is issued.',
            readOnly: 'Only a superadmin can change the issuer.',
            saved: 'Issuer details saved.',
        },
        service: {
            title: 'E-Invoice Service',
            hint: 'The service invoices are issued through. Connect it, check it, then choose it. Whether it issues on its test or its live side is set for the whole deployment.',
            notChosen: 'Until a checked e-invoice service is chosen, paid packages wait for their invoice.',
            empty: 'No e-invoice service is connected.',
            connect: 'Connect an E-Invoice Service',
            verify: 'Check the Connection',
            use: 'Use for Invoices',
            stop: 'Stop Issuing Invoices',
            withdraw: 'Remove the Connection',
            inUse: 'In Use',
            status: { pending: 'Not Checked', connected: 'Connected', failed: 'Connection Problem' },
            problem: { unavailable: 'The service could not be reached.', failed: 'The check did not pass.' },
            issues: { both: 'e-Invoice and e-Archive', einvoice: 'e-Invoice only', earchive: 'e-Archive only' },
            settingsHint: 'What the service lets you decide about the invoices.',
            back: 'Back to the Services',
            choose: 'Connect',
            connected: 'Connected',
            notReady: 'Not Ready',
            confirmStop: {
                title: 'Invoices Will Stop',
                body: 'Paid packages wait for their invoice until a service is chosen again. Invoices already issued stay as they are.',
                action: 'Stop Issuing Invoices',
            },
            confirmWithdraw: {
                title: 'The Connection Will Be Removed',
                body: 'It issues no new invoice. Its keys are kept, so the documents it issued can still be fetched through it.',
                action: 'Remove the Connection',
            },
            done: {
                connected: 'E-invoice service connected. Check it before choosing it.',
                chosen: 'Invoices are now issued through this service.',
                stopped: 'Invoices stopped.',
                withdrawn: 'Connection removed.',
            },
        },
        offers: {
            title: 'E-Invoice Services Offered to Sellers',
            hint: 'The services a seller can connect in their invoice settings. Offer one once its connection has been proved against a live account. A hidden service refuses new connections; the sellers already connected keep theirs.',
            offered: 'Offered',
            hidden: 'Hidden',
            issues: {
                both: 'e-Invoice and e-Archive',
                einvoice: 'e-Invoice only',
                earchive: 'e-Archive only',
                record: 'Records the sale; the invoice is issued in the service itself',
            },
            sellers: (count: number) => (count === 0 ? 'No seller connected' : count === 1 ? '1 seller connected' : `${count} sellers connected`),
            open: 'Offer to Sellers',
            close: 'Hide from Sellers',
            readOnly: 'Only a superadmin can offer a service to sellers or hide it.',
            confirmOpen: {
                title: (service: string) => `${service} Will Be Offered to Sellers`,
                body: 'Every seller can connect it from their invoice settings. Offer it only once its connection has been proved against a live account.',
                action: 'Offer to Sellers',
            },
            confirmClose: {
                title: (service: string) => `${service} Will Be Hidden from Sellers`,
                body: (sellers: number) =>
                    sellers === 0
                        ? 'Sellers can no longer connect it.'
                        : `Sellers can no longer connect it. The ${sellers === 1 ? 'seller' : `${sellers} sellers`} already connected keep the connection and the invoices issued through it.`,
                action: 'Hide from Sellers',
            },
            done: { opened: 'The service is now offered to sellers.', hidden: 'The service is now hidden from sellers.' },
        },
    },

    // The connection form's own words; the fields come from the service (ADR 0011 §2).
    providerForm: {
        name: 'Connection Name',
        nameHint: 'Your own label, for when there is more than one.',
        environment: 'Environment',
        sandbox: 'Test',
        production: 'Live',
        testNote: (service: string) => `${service} connects to its test side: no real payment is taken through it.`,
        useDefault: 'Default',
        submit: 'Save and Connect',
    },
} as const;

/** The message contract every locale must satisfy. */
type Translated<Source> = Source extends (...args: infer Args) => string
    ? (...args: Args) => string
    : Source extends string
      ? string
      : { readonly [Key in keyof Source]: Translated<Source[Key]> };

export type Messages = Translated<typeof en>;

const tr: Messages = {
    loading: 'Yükleniyor…',
    genericError: 'Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.',
    signOut: 'Çıkış Yap',
    panelName: 'Admin Panel',

    errors: {
        status: {
            401: 'Oturumunuz sona erdi. Yeniden giriş yapın.',
            403: 'Bu işlem için yetkiniz yok.',
            404: 'Kayıt bulunamadı. Sayfayı yenileyip yeniden deneyin.',
            409: 'Bu işlem mevcut bir kayıtla çakışıyor. Bilgileri kontrol edip yeniden deneyin.',
            422: 'Gönderilen bilgiler kabul edilmedi. Bilgileri kontrol edip yeniden deneyin.',
            429: 'Çok fazla istek gönderildi. Biraz bekleyip yeniden deneyin.',
        },
        problems: {
            'auth.signed_out': 'Oturumunuz sona erdi. Yeniden giriş yapın.',
            'auth.no_company': 'Önce bir firma seçin.',
            'auth.forbidden': 'Bu işlem için yetkiniz yok.',
            'auth.not_in_plan': 'Bu, firmanın paketine dahil değil.',
            'auth.invalid_credentials': 'E-posta veya parola hatalı.',
            'auth.session_expired': 'Oturumunuz sona erdi. Yeniden giriş yapın.',
            'auth.second_factor_invalid':
                'Kod doğru değil. Doğrulama uygulamanızın şu an gösterdiği kodu ya da kullanılmamış bir kurtarma kodunu girin.',
            'auth.second_factor_expired': 'Bu girişin süresi doldu. Parolanızla yeniden giriş yapın.',
            'auth.two_factor_enabled': 'İki adımlı doğrulama zaten açık.',
            'auth.two_factor_not_started': 'İki adımlı doğrulama kurulumunu yeniden başlatın.',
            'admin.already_impersonating': 'Zaten bir hesabın içinde işlem yapıyorsunuz. Başka bir hesaba girmeden önce ondan çıkın.',
            'admin.no_member_to_act_as': 'Bu hesapta adına işlem yapılabilecek etkin bir üye yok.',
            'admin.staff_user_not_found': 'Bu e-posta adresiyle bir hesap yok. Önce kayıt olması gerekir.',
            'admin.cannot_revoke_self': 'Kendi personel yetkinizi kaldıramazsınız.',
            'admin.status_not_allowed': 'Bu durum burada ayarlanamaz.',
            'admin.account_erased': 'Bu hesabın kişisel verileri silindi; hesap yeniden açılamaz.',
            'admin.own_status': 'Kendi durumunuzu değiştiremezsiniz.',
            'admin.plan_key_taken': 'Bu anahtarla bir paket zaten var.',
            'admin.not_trialing': 'Yalnızca süren bir deneme uzatılabilir. Sayfayı yenileyin.',
            'admin.reason_required': 'Gerekçeyi yazın; denetim kaydında saklanır.',
            'admin.trial_days': 'Deneme 1 ile 90 gün arasında uzatılabilir.',
            'admin.default_not_offered': 'Yeni hesapların başladığı paket satışta olmalı. Yeni Hesaplara Satışta seçeneğini de işaretleyin.',
            'admin.default_required': 'Yeni hesaplar her zaman bir pakette başlar. Önce başka bir paketi varsayılan yapın.',
            'admin.price_basis_unknown': 'Fiyatların KDV’yi içerip içermediğini seçin.',
            'admin.subscription_not_lapsed':
                'Ödeme Bekliyor ve Süresi Doldu, dönemi bitmiş abonelik içindir: Dönem Bitişi geçmiş bir tarih olmalı, Deneme Bitişi ileride olmamalı. Hesabı ileri bir tarihe kadar açmak için durumu Aktif seçin.',
            'admin.subscription_lapsed':
                'Deneme ve Aktif, dönemi sürmekte olan abonelik içindir: Dönem Bitişi’ni ileri bir tarih seçin ya da boş bırakın. Aksi hâlde hesap bir saat içinde Ödeme Bekliyor’a geçer.',
            'admin.cancel_passed':
                'Süren bir abonelik İptal Tarihi’nde sona erer: ileri bir tarih seçin ya da boş bırakın. Aksi hâlde hesap bir saat içinde Süresi Doldu’ya geçer.',
            'admin.feature_level': 'Bu özellik bu düzeyi sunmuyor.',
            'auth.subscription_expired':
                'Firmanın aboneliğinin süresi doldu: siparişleri, faturaları ve stoku çalışmaya devam eder; ödeme yapılana dek yeni bir şey eklenmez.',
            'auth.terms_not_accepted': 'Bu oturumda güncel Kullanım Koşulları onaylanmamış. Koşulları satıcı, satıcı panelinde onaylar.',
            'admin.charge_not_pending': 'Bu ödeme artık beklemiyor. Listeyi yenileyin.',
            'admin.charge_held':
                'Ödeme alındı olarak kaydedildi ama uygulanmadı: hesapta bekleyen bir paket değişikliği var. Ödeme Bekletiliyor olarak listelenir; değişiklik uygulandıktan sonra ödemeyi uygulayın ya da iade edin.',
            'admin.charge_not_paid': 'Yalnızca ödenmiş ya da bekletilen bir ödeme iade edilebilir.',
            'admin.charge_not_held': 'Bu ödeme artık bekletilmiyor. Listeyi yenileyin.',
            'admin.change_waiting':
                'Hesapta hâlâ bekleyen bir paket değişikliği var. Ödemeyi, değişiklik uygulandıktan ya da paket hesabın sayfasından belirlendikten sonra uygulayın veya iade edin.',
            'admin.bank_details_incomplete': 'Hesap sahibi, banka ve IBAN birlikte girilmeli ya da üçü birden boşaltılmalı.',
            'admin.iban_invalid': 'Bu IBAN geçerli değil. Bankanın verdiği numarayla karşılaştırın.',
            'admin.charge_by_card': 'Kartla yapılan ödeme, ödeme hizmetinin bildirimiyle kesinleşir; elle onaylanmaz.',
            'admin.refund_refused': (quote: string) => `Ödeme hizmeti iadeyi reddetti: ${quote}`,
            'admin.payment_service_unavailable': 'Ödeme hizmetine şu an ulaşılamıyor. Birkaç dakika sonra yeniden deneyin.',
            'admin.payment_provider_unknown': 'Bu ödeme hizmeti burada kullanılamaz.',
            'admin.payment_provider_not_ready': (quote: string) => quote,
            'admin.payment_credential_missing': 'Zorunlu bağlantı bilgilerinin hepsini girin.',
            'admin.payment_setting_unknown': 'Ödeme hizmetinin sunduğu değerlerden birini seçin.',
            'admin.payment_environment': 'Ödeme hizmetinin böyle bir ortamı yok. Hizmetin sunduğu Test ya da Canlı ortamı seçin.',
            'admin.payment_already_connected': 'Bu ödeme hizmeti zaten bağlı.',
            'admin.payment_not_connected': 'Önce bağlantıyı kontrol edin: kartla ödemede yalnızca son kontrolü geçen bağlantı kullanılabilir.',
            'admin.issuer_incomplete': 'Unvan, vergi numarası, vergi dairesi ve adresin tamamı birlikte girilmeli ya da hepsi boşaltılmalı.',
            'admin.issuer_tax_number': 'Bu vergi numarası geçerli değil. Vergi levhasındaki numarayla karşılaştırın.',
            'admin.issuer_mersis_invalid': 'MERSİS numarası on altı hanedir. Ticaret sicili kaydıyla karşılaştırın.',
            'admin.issuer_kep_invalid': 'Bu KEP adresi geçerli değil. Size verilen KEP adresini eksiksiz yazın.',
            'admin.invoicing_off':
                'Henüz fatura kesilemiyor. Fatura Ayarları’ndan kontrolü geçmiş bir e-Fatura hizmeti seçin ve düzenleyen bilgilerini tamamlayın.',
            'admin.invoice_not_open': 'Bu ödemenin bekleyen faturası yok. Listeyi yenileyin.',
            'admin.invoice_provider_unknown': 'Bu e-Fatura hizmeti burada kullanılamaz.',
            'admin.invoice_provider_not_ready': (quote: string) => quote,
            'admin.invoice_credential_missing': 'Zorunlu bağlantı bilgilerinin hepsini girin.',
            'admin.invoice_setting_unknown': 'e-Fatura hizmetinin sunduğu değerlerden birini seçin.',
            'admin.invoice_already_connected': 'Bu e-Fatura hizmeti zaten bağlı.',
            'admin.invoice_not_connected': 'Önce bağlantıyı kontrol edin: fatura yalnızca son kontrolü geçen bağlantıyla kesilebilir.',
        },
    },

    nav: {
        menu: 'Menü',
        overview: 'Genel Bakış',
        accounts: 'Hesaplar',
        users: 'Kişiler',
        requests: 'Talepler',
        audit: 'Denetim Kaydı',
        system: 'Sistem',
        plans: 'Paketler',
        revenue: 'Gelir',
        payments: 'Ödemeler',
        paymentSettings: 'Ödeme Ayarları',
        invoiceSettings: 'Fatura Ayarları',
        announcements: 'Duyurular',
        staff: 'Personel',
        account: 'Hesabım',
    },

    navGroups: {
        sellers: 'Satıcılar',
        subscriptions: 'Abonelik',
        administration: 'Yönetim',
    },

    common: {
        datePlaceholder: 'gg.aa.yyyy',
        pickDate: 'Takvimden Seç',
        confirm: 'Onayla',
        cancel: 'Vazgeç',
        close: 'Kapat',
        all: 'Tümü',
        never: 'Hiç',
        notFound: {
            title: 'Sayfa Bulunamadı',
            body: 'Adres yanlış yazılmış ya da sayfa taşınmış olabilir.',
            home: 'Ana Sayfaya Dön',
        },
        forbidden: {
            title: 'Bu Sayfaya Erişiminiz Yok',
            body: 'Personel seviyeniz bu sayfayı kapsamıyor. Seviyenizi bir süper yönetici değiştirebilir.',
            home: 'Genel Bakışa Dön',
        },
        error: {
            title: 'Sayfa Görüntülenemedi',
            body: 'Beklenmeyen bir sorun oluştu. Tekrar deneyin; sorun sürerse sayfayı yenileyin.',
            retry: 'Tekrar Dene',
            home: 'Ana Sayfaya Dön',
        },
        no: 'Hayır',
        save: 'Kaydet',
        previous: 'Önceki',
        next: 'Sonraki',
        pageOf: (from: number, to: number, total: number) => `${total} kayıttan ${from}–${to}`,
        language: 'Dil',
        theme: { label: 'Tema', light: 'Açık', dark: 'Koyu', system: 'Sistem' },
    },

    auth: {
        title: 'Platform Girişi',
        subtitle: 'Entegrepazar personeli içindir. Satıcılar satıcı panelinden girer.',
        email: 'E-posta',
        password: 'Parola',
        submit: 'Giriş Yap',
        invalid: 'E-posta veya parola hatalı.',
        notStaff: 'Bu hesap platform personeli değil.',
        secondFactor: {
            title: 'İki Adımlı Doğrulama',
            hint: 'Doğrulama uygulamanızın gösterdiği kodu girin. Telefonunuz yanınızda değilse kurtarma kodlarınızdan birini girin.',
            code: 'Doğrulama Kodu',
            submit: 'Doğrula',
            startOver: 'Yeniden Giriş Yap',
        },
    },

    account: {
        title: 'Hesabım',
        subtitle: 'Platform paneline nasıl giriş yaptığınız.',
        twoFactor: {
            title: 'İki Adımlı Doğrulama',
            hint: 'Girişte parolanızın yanında telefonunuzdaki doğrulama uygulamasının gösterdiği kod da istenir.',
            on: 'Açık',
            off: 'Kapalı',
            enable: 'Aç',
            disable: 'Kapat',
            setupTitle: 'İki Adımlı Doğrulamayı Aç',
            scan: 'Bu kodu Google Authenticator veya Microsoft Authenticator gibi bir doğrulama uygulamasıyla okutun, ardından uygulamanın gösterdiği kodu girin.',
            qrLabel: 'Doğrulama uygulaması için QR kod',
            setupKey: 'Kurulum Anahtarı',
            setupKeyHint: 'Kodu okutamıyorsanız bu anahtarı uygulamaya girin.',
            code: 'Doğrulama Kodu',
            confirm: 'Aç',
            recoveryTitle: 'Kurtarma Kodlarınız',
            recoveryHint:
                'Telefonunuzu kaybederseniz her kod bir kez giriş yapmanızı sağlar. Güvenli bir yerde saklayın: kodlar yalnızca şimdi gösterilir.',
            done: 'Kaydettim',
            disableTitle: 'İki Adımlı Doğrulamayı Kapat',
            disableHint: 'Parolanızı ve doğrulama uygulamanızdaki kodu ya da bir kurtarma kodunu girin.',
            password: 'Parola',
            disableConfirm: 'Kapat',
            copy: { copy: 'Kopyala', copied: 'Kopyalandı' },
        },
    },

    overview: {
        title: 'Genel Bakış',
        subtitle: 'Tüm hesaplar ve içlerinde çalışmayanlar.',
        accounts: 'Hesap',
        activeAccounts: 'Aktif',
        users: 'Kullanıcı',
        products: 'Ürün',
        orders: 'Sipariş',
        failingChannels: 'Çalışmayan Bağlantı',
        failing: 'Çalışmayı Bırakan Bağlantılar',
        failingEmpty: 'Tüm Bağlantılar Çalışıyor',
        failingHint: 'Bu platformdaki en sessiz arıza: görünürde bir şey bozulmaz, siparişler gelmeyi bırakır.',
        overLimit: 'Sınırı Aşan Hesaplar',
        overLimitHint:
            'Paketine dahil olanın üzerindeki kullanım. Hiçbir şey silinmez; daha büyük bir pakete geçene ya da kullanımını azaltana dek yenisini ekleyemez.',
        overLimitEmpty: 'Sınırı Aşan Hesap Yok',
        lapsed: 'Aboneliğinin Süresi Dolan Hesaplar',
        lapsedHint: 'Siparişleri, faturaları ve stoku çalışmaya devam eder; yeni bir şey eklenemez. Dönemin bitişinden yedi gün sonra arayın.',
        lapsedEmpty: 'Süresi Dolan Abonelik Yok',
        columns: {
            package: 'Paket',
            limit: 'Sınır',
            usage: 'Kullanım',
            contact: 'İletişim',
            periodEnd: 'Dönem Bitişi',
            since: 'Geçen Süre',
        },
        daysSince: (days: number) => `${days} gün`,
        callNow: 'Arama Zamanı',
    },

    accounts: {
        title: 'Hesaplar',
        subtitle: 'Platformdaki tüm satıcılar.',
        search: 'İsimle ara',
        empty: 'Bu Aramayla Eşleşen Hesap Yok',
        columns: {
            account: 'Hesap',
            status: 'Durum',
            plan: 'Paket',
            owner: 'Sahip',
            products: 'Ürün',
            orders: 'Sipariş',
            channels: 'Kanal',
            detail: 'Son Yanıt',
        },
        status: {
            trial: 'Deneme',
            active: 'Aktif',
            suspended: 'Askıda',
            cancelled: 'Kapatıldı',
        },
        subscription: {
            trialing: 'Deneme',
            active: 'Aktif',
            past_due: 'Ödeme Bekliyor',
            canceled: 'İptal',
            expired: 'Süresi Doldu',
        },
        filterStatus: 'Durum',
        noPlan: 'Paket Yok',
        trialEnds: (date: string) => `${date} bitiyor`,
        enter: 'Hesaba Gir',
        manage: 'Yönet',
        confirmEnter: {
            title: 'Bu Hesaba Girilsin mi?',
            body: 'Verilerini göreceksiniz, satıcıya uyarı çıkar ve yaptığınız her işlem sizin adınıza kaydedilir.',
            action: 'Destek Oturumu Aç',
        },
        handoffFailed: 'Destek oturumu açılamadı.',
    },

    revenue: {
        title: 'Gelir',
        subtitle: 'Platform ne kazanıyor ve kimler ayrılıyor.',
        mrr: 'Aylık Yinelenen Gelir',
        mrrHint: 'Şu an ödenen paketlerin toplamı.',
        churn: 'Ayrılan (30 Gün)',
        churnHint: 'Son 30 günde sona eren abonelikler.',
        active: 'Aktif',
        trialing: 'Deneme',
        pastDue: 'Ödeme Bekliyor',
        canceled: 'İptal',
        expired: 'Süresi Doldu',
        mix: 'Abonelik Dağılımı',
        distribution: 'Paketler',
        columns: {
            plan: 'Paket',
            price: 'Aylık Ücret',
            subscribers: 'Abone',
            active: 'Aktif',
        },
    },

    announcements: {
        title: 'Duyurular',
        subtitle: 'Tüm satıcılara yayın. Her biri kendi kutusunda bildirim olarak görür.',
        send: 'Yayın Gönder',
        titleField: 'Başlık',
        body: 'Mesaj',
        level: 'Seviye',
        submit: 'Herkese Gönder',
        history: 'Geçmiş',
        empty: 'Henüz Yayın Gönderilmedi',
        audience: 'Ulaşılan',
        when: 'Gönderildi',
        levels: {
            info: 'Bilgi',
            success: 'Başarı',
            warning: 'Uyarı',
            error: 'Hata',
        },
    },

    feature: {
        names: {
            storefront: 'Tezgah',
            invoicing: 'e-Fatura',
            price_rules: 'Fiyatlandırma',
            suppliers: 'Tedarikçiler',
            integrations: 'API Erişimi ve Webhook’lar',
            finance: 'Finans',
        },
        levels: {
            off: 'Kapalı',
            read: 'Salt Okunur',
            full: 'Tam Erişim',
        },
    },

    limits: {
        products: 'Ürün',
        channels: 'Pazaryeri Hesabı',
        users: 'Kullanıcı',
        orders_monthly: 'Bu Ayki Sipariş',
    },

    accountDetail: {
        back: 'Hesaplara Dön',
        notFound: 'Böyle bir hesap yok.',
        tabs: {
            overview: 'Özet',
            people: 'Kişiler',
            channels: 'Bağlantılar',
            access: 'Erişim',
            audit: 'Denetim',
        },
        since: (date: string) => `${date} tarihinden beri`,
        suspend: 'Askıya Al',
        reactivate: 'Yeniden Aç',
        confirmSuspend: {
            title: 'Bu Hesap Askıya Alınsın mı?',
            body: 'İçindeki herkesin oturumu anında kapanır ve yeniden giremezler. Hiçbir şey silinmez; yeniden açınca erişim geri gelir.',
            action: 'Askıya Al',
        },
        confirmReactivate: {
            title: 'Bu Hesap Yeniden Açılsın mı?',
            body: 'Üyeleri hemen yeniden giriş yapabilir.',
            action: 'Yeniden Aç',
        },
        close: 'Hesabı Kapat',
        confirmClose: {
            title: 'Hesap Kapatılacak',
            body: 'İçindeki herkesin oturumu kapanır ve yeniden giremezler. Kapanıştan bir yıl sonra kişisel veriler kalıcı olarak silinir: kişiler, müşteriler, bağlantılar ve iletişim bilgileri. Faturalar ve muhasebe kayıtları saklanır. O güne kadar hesap yeniden açılabilir.',
            action: 'Hesabı Kapat',
        },
        closedOn: (date: string) =>
            `${date} tarihinde kapatıldı. Kişisel veriler kapanıştan bir yıl sonra silinir; o güne kadar hesap yeniden açılabilir.`,
        erasedOn: (date: string) => `Kişisel veriler ${date} tarihinde silindi. Faturalar ve muhasebe kayıtları saklanıyor.`,
        statusChanged: 'Hesap durumu güncellendi.',
        summary: 'Özet',
        owner: 'Sahip',
        noOwner: 'Aktif sahip yok',
        legalName: 'Ticari Unvan',
        taxNumber: 'Vergi Numarası',
        taxOffice: 'Vergi Dairesi',
        counts: {
            users: 'Kişi',
            products: 'Ürün',
            orders: 'Sipariş',
            channels: 'Kanal',
        },
        lastOrder: 'Son Sipariş',
        shop: 'Mağaza',
        shopPublished: 'Yayında',
        shopDraft: 'Yayında değil',
        noShop: 'Mağaza yok',
        subscription: 'Abonelik',
        subscriptionStatus: 'Durum',
        trialEnds: 'Deneme Bitişi',
        periodEnd: 'Dönem Bitişi',
        cancelAt: 'İptal Tarihi',
        people: 'Kişiler',
        peopleEmpty: 'Bu Hesapta Kimse Yok',
        peopleColumns: {
            person: 'Kişi',
            role: 'Rol',
            status: 'Durum',
            verified: 'E-posta',
            lastLogin: 'Son Giriş',
        },
        verified: 'Doğrulanmış',
        unverified: 'Doğrulanmamış',
        terms: 'Kullanım Koşulları Onayları',
        termsHint: 'Kayıtta ve biri yeni bir sürümü onayladığında kaydedilir. Yalnızca görüntülenir.',
        termsEmpty: 'Kayıtlı Onay Yok',
        termsColumns: {
            person: 'Kişi',
            terms: 'Kullanım Koşulları',
            notice: 'Aydınlatma Metni',
            acceptedAt: 'Onay Zamanı',
            client: 'IP Adresi ve Tarayıcı',
        },
        version: (date: string) => `${date} sürümü`,
        memberStatus: {
            invited: 'Davetli',
            active: 'Aktif',
            disabled: 'Çıkarılmış',
        },
        channels: 'Bağlantılar',
        channelsEmpty: 'Pazaryeri Bağlantısı Yok',
        channelColumns: {
            channel: 'Kanal',
            status: 'Durum',
            verified: 'Doğrulama',
            detail: 'Son Yanıt',
        },
        channelStatus: {
            pending: 'Bekliyor',
            connected: 'Çalışıyor',
            failed: 'Başarısız',
            disabled: 'Kapalı',
        },
        sandbox: 'Test Ortamı',
        channelProblem: {
            auth: 'Mağaza bilgileri kabul edilmedi.',
            validation: 'Pazaryeri gönderilen bilgileri kabul etmedi.',
            conflict: 'Kayıt bu arada pazaryerinde değişmiş.',
            not_found: 'Pazaryeri bu kaydı bulamadı.',
            deprecated: 'Pazaryeri bu işlemi artık desteklemiyor.',
            rate_limited: 'Pazaryeri çok fazla istek aldı.',
            transient: 'Pazaryerine ulaşılamadı.',
            channel_down: 'Pazaryerine ulaşılamadı.',
            unknown: 'Beklenmeyen bir sorun oluştu.',
        },
        handoffs: 'Destek Oturumları',
        handoffsHint: 'Personel bu hesaba her girdiğinde: kim, ne zaman.',
        handoffsEmpty: 'Platformdan Kimse Bu Hesaba Girmedi',
        handoffColumns: {
            staff: 'Personel',
            actedAs: 'Kimin Adına',
            when: 'Ne Zaman',
            used: 'Kullanım',
        },
        unused: 'Kullanılmadı',
        audit: 'Denetim Kaydı',
        auditHint: 'Bu hesabın içinde olanlar, en yeniden başlayarak.',
        plan: 'Paket',
        planHint: 'Bu hesabı hemen bir pakete taşıyın. Müşterinin kendi yapamayacağı yol budur.',
        planDates: {
            trialEndsAt: 'Deneme Bitişi',
            periodEnd: 'Dönem Bitişi',
            cancelAt: 'İptal Tarihi',
            hint: 'Boş bırakılan tarih temizlenir.',
        },
        planKey: 'Paket',
        status: 'Durum',
        apply: 'Paketi Uygula',
        planApplied: 'Paket güncellendi.',
        fitWithin: 'Kullanım bu paketin sınırları içinde; kapanan özellik yok.',
        fitExceeds: 'Kullanım şu sınırların üzerinde kalır:',
        fitLoses: 'Şu özellikler kapanır ya da daralır:',
        usage: 'Paket Kullanımı',
        usageHint: 'Faturalamanın saydığı gibi; paketin boş bıraktığı sınır gösterilmez.',
        usageNone: 'Bu paket sınır koymuyor.',
        overLimit: 'Sınırın üzerinde',
        scheduled: (date: string, plan: string) => `${date} tarihinde ${plan} paketine geçilecek.`,
        extendTrial: 'Denemeyi Uzat',
        extendTitle: 'Denemeyi Uzat',
        extendBody: (date: string) => `Deneme şu an ${date} tarihinde bitiyor. Hesaba bildirilir; gerekçe denetim kaydında saklanır.`,
        extendDays: 'Gün',
        extendDaysHint: '1 ile 90 gün arası; bugünkü bitişe eklenir.',
        extendReason: 'Gerekçe',
        extendAction: 'Denemeyi Uzat',
        extended: (date: string) => `Deneme ${date} tarihine uzatıldı.`,
        features: 'Özellik Düzeyleri',
        featuresHint: 'Ürünün her bölümünün bu hesap için düzeyi: paketindeki ya da yalnızca bu hesap için burada verilen. Müşteri değiştiremez.',
        featureColumns: {
            feature: 'Özellik',
            plan: 'Pakette',
            own: 'Bu Hesaba Özel',
            effective: 'Geçerli',
        },
        change: 'Değiştir',
        levelTitle: (feature: string) => `Bu Hesap İçin ${feature}`,
        level: 'Düzey',
        asPlan: (level: string) => `Paketteki Gibi (${level})`,
        until: 'Bitiş Tarihi',
        untilHint: 'Süresiz için boş bırakın. Bittiğinde paketteki düzey geçerli olur.',
        lapsed: 'Süresi Doldu',
        note: 'Not (Opsiyonel)',
        save: 'Düzeyi Kaydet',
    },

    staff: {
        title: 'Personel',
        subtitle: 'Platformda kimler çalışıyor ve ne yapabiliyorlar.',
        empty: 'Henüz Platform Personeli Yok',
        columns: {
            level: 'Seviye',
        },
        level: {
            support: 'Destek',
            admin: 'Yönetici',
            superadmin: 'Süper Yönetici',
        },
        levelHint: {
            support: 'Platform ekranlarını görebilir.',
            admin: 'Ayrıca bir müşteri hesabına girebilir.',
            superadmin: 'Ayrıca kimin personel olacağına karar verebilir.',
        },
        add: 'Personel Ekle',
        email: 'Mevcut Bir Hesabın E-postası',
        emailHint: 'Önce kayıt olmuş olmalı. Yetki vermek hesap oluşturmaz.',
        grant: 'Yetki Ver',
        revoke: 'Kaldır',
        confirmRevoke: {
            title: 'Platform Yetkisi Kaldırılsın mı?',
            body: 'Oturumları anında sona erer.',
            action: 'Kaldır',
        },
    },

    users: {
        title: 'Kişiler',
        subtitle: 'Tüm hesaplardaki, giriş yapabilen herkes.',
        search: 'İsim veya e-posta ile ara',
        empty: 'Bu Aramayla Eşleşen Kimse Yok',
        columns: {
            person: 'Kişi',
            accounts: 'Hesaplar',
            status: 'Durum',
            verified: 'E-posta',
            lastLogin: 'Son Giriş',
            created: 'Kayıt',
        },
        status: { active: 'Aktif', disabled: 'Devre Dışı' },
        staff: 'Personel',
        disable: 'Devre Dışı Bırak',
        enable: 'Etkinleştir',
        confirmDisable: {
            title: 'Bu Kişi Devre Dışı Bırakılsın mı?',
            body: 'Her yerdeki oturumu anında kapanır ve yeniden etkinleştirilene kadar giriş yapamaz. Hesapları ve verileri kalır.',
            action: 'Devre Dışı Bırak',
        },
        confirmEnable: {
            title: 'Bu Kişi Yeniden Etkinleştirilsin mi?',
            body: 'Hemen yeniden giriş yapabilir.',
            action: 'Etkinleştir',
        },
        resetTwoFactor: 'İki Adımlı Doğrulamayı Sıfırla',
        confirmResetTwoFactor: {
            title: 'İki Adımlı Doğrulama Sıfırlanacak',
            body: 'Doğrulama uygulaması ve kurtarma kodları artık çalışmaz, kişinin tüm oturumları kapanır. Yalnızca parolasıyla giriş yapar ve doğrulamayı yeniden kurabilir.',
            action: 'Sıfırla',
        },
    },

    audit: {
        title: 'Denetim Kaydı',
        subtitle: 'Platformda kim ne yaptı. En yeniden başlayarak.',
        search: 'İşlem, kişi veya nesne ile ara',
        empty: 'Eşleşen Kayıt Yok',
        columns: {
            when: 'Ne Zaman',
            actor: 'Kim',
            action: 'Ne Yaptı',
            entity: 'Neye',
            changes: 'Ne Değişti',
            account: 'Hesap',
            ip: 'Nereden',
        },
        actorType: {
            user: 'Kullanıcı',
            system: 'Sistem',
            api_token: 'API Anahtarı',
            platform_admin: 'Platform Personeli',
        },
        viaSupport: (staff: string) => `Destek: ${staff}`,
        actions: {
            'subscription.changed': 'Abonelik değiştirildi',
            'plan.created': 'Paket oluşturuldu',
            'plan.updated': 'Paket güncellendi',
            'plan.availability_changed': 'Paketin satışı değiştirildi',
            'subscription.trial_extended': 'Deneme uzatıldı',
            'subscription.cancelled': 'Abonelik iptal edildi',
            'subscription.resumed': 'Abonelik iptali geri alındı',
            'billing_settings.vat_basis_changed': 'Fiyatlarda KDV esası değiştirildi',
            'tenant.registered': 'Hesap oluşturuldu',
            'tenant.updated': 'Hesap bilgileri güncellendi',
            'tenant.suspended': 'Hesap askıya alındı',
            'tenant.reactivated': 'Hesap yeniden açıldı',
            'tenant.closed': 'Hesap kapatıldı',
            'tenant_feature.changed': 'Özellik düzeyi değiştirildi',
            'tenant_feature.cleared': 'Özellik düzeyi pakete bırakıldı',
            'plan_feature.changed': 'Paketin özellik düzeyi değiştirildi',
            'user.disabled': 'Kişinin erişimi kapatıldı',
            'user.enabled': 'Kişinin erişimi açıldı',
            'user.profile_updated': 'Profil güncellendi',
            'user.password_changed': 'Parola değiştirildi',
            'user.password_reset': 'Parola sıfırlandı',
            'user.email_changed': 'E-posta değiştirildi',
            'terms.accepted': 'Kullanım Koşulları kabul edildi',
            'auth.signed_in': 'Giriş yapıldı',
            'auth.signed_out': 'Çıkış yapıldı',
            'auth.email_verified': 'E-posta doğrulandı',
            'auth.support_handoff_issued': 'Destek oturumu açıldı',
            'auth.impersonation_started': 'Destek için hesaba girildi',
            'team.invited': 'Ekibe davet edildi',
            'team.invitation_revoked': 'Davet geri alındı',
            'team.invitation_accepted': 'Davet kabul edildi',
            'team.member_updated': 'Ekip üyesi güncellendi',
            'team.role_saved': 'Rol kaydedildi',
            'team.role_deleted': 'Rol silindi',
            'platform.staff_granted': 'Personel yetkisi verildi',
            'platform.staff_revoked': 'Personel yetkisi kaldırıldı',
            'request.updated': 'Talep güncellendi',
            'announcement.created': 'Duyuru yayınlandı',
            'billing_charge.paid': 'Ödeme alındı',
            'billing_charge.held': 'Ödeme alındı, uygulanmadı',
            'billing_charge.applied': 'Ödeme uygulandı',
            'billing_charge.cancelled': 'Ödeme iptal edildi',
            'billing_charge.refunded': 'Ödeme iade edildi',
            'billing_charge.invoiced': 'Ödemenin faturası kesildi',
            'billing_charge.invoice_failed': 'Ödemenin faturası kesilemedi',
            'billing_charge.invoice_recorded': 'Başka yerde kesilen fatura kaydedildi',
            'billing_settings.changed': 'Havale bilgileri değiştirildi',
            'billing_issuer.changed': 'Düzenleyen bilgileri değiştirildi',
            'billing_charge.failed': 'Kartla ödeme reddedildi',
            'platform_payment_account.connected': 'Ödeme hizmeti bağlandı',
            'platform_payment_account.used': 'Ödeme hizmeti kartla ödemede kullanılmaya başlandı',
            'platform_payment_account.unused': 'Kartla ödeme kapatıldı',
            'platform_payment_account.withdrawn': 'Ödeme hizmeti bağlantısı kaldırıldı',
            'platform_invoice_account.connected': 'Platformun e-Fatura hizmeti bağlandı',
            'platform_invoice_account.used': 'e-Fatura hizmeti faturalarda kullanılmaya başlandı',
            'platform_invoice_account.unused': 'Fatura kesme durduruldu',
            'platform_invoice_account.withdrawn': 'Platformun e-Fatura hizmeti bağlantısı kaldırıldı',
            'invoice_provider.opened': 'e-Fatura hizmeti satıcılara açıldı',
            'invoice_provider.closed': 'e-Fatura hizmeti satıcılardan gizlendi',
            'auth.sign_in_failed': 'Giriş başarısız oldu',
            'auth.recovery_code_used': 'Kurtarma koduyla giriş yapıldı',
            'auth.two_factor_enabled': 'İki adımlı doğrulama açıldı',
            'auth.two_factor_disabled': 'İki adımlı doğrulama kapatıldı',
            'platform.two_factor_reset': 'İki adımlı doğrulama sıfırlandı',
            'api_token.created': 'API anahtarı oluşturuldu',
            'api_token.revoked': 'API anahtarı iptal edildi',
            'webhook_endpoint.created': 'Bildirim adresi eklendi',
            'webhook_endpoint.deleted': 'Bildirim adresi silindi',
            'channel_account.connected': 'Satış kanalı bağlandı',
            'channel_account.credentials_changed': 'Satış kanalının erişim bilgileri değiştirildi',
            'invoice_account.connected': 'e-Fatura hizmeti bağlandı',
            'invoice_account.credentials_changed': 'e-Fatura hizmetinin erişim bilgileri değiştirildi',
            'carrier_account.connected': 'Kargo entegrasyonu bağlandı',
            'payment_account.connected': 'Mağazanın ödeme hizmeti bağlandı',
            'payment_account.credentials_changed': 'Mağazanın ödeme hizmeti anahtarları değiştirildi',
            'sms_account.connected': 'SMS hizmeti bağlandı',
            'sms_account.updated': 'SMS hizmeti düzenlendi',
            'feed.password_changed': 'Ürün listesinin parolası değiştirildi',
            'feed.password_removed': 'Ürün listesinin parolası kaldırıldı',
            'listing.bulk_applied': 'Fiyat ve stok toplu düzenlendi',
            'listing.match_approved': 'Kanalın ürün eşleşmesi onaylandı',
            'listing.match_rejected': 'Kanalın ürün eşleşmesi reddedildi',
            'listing.price_lock_released': 'Kanaldaki fiyat kilidi kaldırıldı',
            'inventory.bulk_adjusted': 'Stok toplu düzenlendi',
            'inventory.warehouse_transferred': 'Deponun stoğu başka depoya taşındı',
            'inventory.warehouse_paused': 'Depo duraklatıldı: stoğu kanallardan çekildi',
            'inventory.warehouse_resumed': 'Depo yeniden satışa açıldı: stoğu kanallara geri verildi',
            'inventory.stock_transferred': 'Bir varyantın stoğunun bir kısmı başka depoya taşındı',
            'product.bulk_status_changed': 'Ürünlerin durumu toplu değiştirildi',
            'product.bulk_category_changed': 'Ürünlerin kategorisi toplu değiştirildi',
            'product.bulk_reverted': 'Toplu düzenleme geri alındı',
            'product.bulk_deleted': 'Ürünler toplu silindi',
            'brand.updated': 'Marka düzenlendi',
            'brand.merged': 'Markalar birleştirildi',
            'brand.deleted': 'Marka silindi',
            'order.status_requested': 'Sipariş durumu kanala gönderildi',
            'order.cancellation_requested': 'Sipariş iptali kanala gönderildi',
            'order.split_requested': 'Paket bölme kanala gönderildi',
            'order.carrier_change_requested': 'Kargo firması değişikliği kanala gönderildi',
            'order.bulk_status_requested': 'Birden çok siparişin durumu gönderildi',
            'claim.opened': 'İade veya iptal kaydedildi',
            'claim.decided': 'İade veya iptal talebi yanıtlandı',
            'claim.pre_approved': 'Pazaryeri talebine ön onay verildi',
            'channel_account.removed': 'Satış kanalı kaldırıldı',
            'invoice_account.removed': 'e-Fatura hizmeti kaldırıldı',
            'carrier_account.removed': 'Kargo bağlantısı kaldırıldı',
            'payment_account.removed': 'Mağazanın ödeme hizmeti kaldırıldı',
            'sms_account.disconnected': 'SMS hizmeti kaldırıldı',
            'product.updated': 'Ürün düzenlendi',
            'product.prices_changed': 'Ürünün fiyatları değiştirildi',
            'product.imported': 'Ürünler dosyadan aktarıldı',
            'inventory.adjusted': 'Stok düzenlendi',
            'listing.offer_changed': 'Ürünün kanaldaki fiyatı ve stoğu düzenlendi',
            'listing.rows_edited': 'Fiyat ve stok tablosu düzenlendi',
            'listing.sheet_imported': 'Fiyat ve stok Excel ile düzenlendi',
            'shipment.booked': 'Kargo kaydı oluşturuldu',
            'invoice.issued': 'Fatura kesildi',
            'invoice.bulk_issued': 'Toplu fatura kesildi',
            'order.payment_received': 'Havale/EFT ödemesi alındı olarak işaretlendi',
        },
        entities: {
            subscription: 'Abonelik',
            plan: 'Paket',
            tenant: 'Hesap',
            tenant_feature: 'Özellik',
            user: 'Kişi',
            session: 'Oturum',
            invitation: 'Davet',
            role: 'Rol',
            platform_request: 'Talep',
            announcement: 'Duyuru',
            billing_charge: 'Ödeme',
            billing_settings: 'Ödeme Ayarları',
            platform_payment_account: 'Ödeme Hizmeti',
            platform_invoice_account: 'Platformun e-Fatura Hizmeti',
            api_token: 'API Anahtarı',
            webhook_endpoint: 'Bildirim Adresi',
            channel_account: 'Satış Kanalı',
            invoice_account: 'e-Fatura Hizmeti',
            invoice_provider: 'e-Fatura Hizmeti',
            carrier_account: 'Kargo Entegrasyonu',
            payment_account: 'Mağazanın Ödeme Hizmeti',
            sms_account: 'SMS Hizmeti',
            feed: 'Ürün Listesi',
            warehouse: 'Depo',
            bulk_edit: 'Toplu Düzenleme',
            product: 'Ürün',
            variant: 'Varyant',
            brand: 'Marka',
            order: 'Sipariş',
            claim: 'İade veya İptal Talebi',
            stock: 'Stok',
            invoice: 'Fatura',
        },
        fields: {
            plan_key: 'Paket',
            status: 'Durum',
            trial_ends_at: 'Deneme Bitişi',
            current_period_end: 'Dönem Bitişi',
            cancel_at: 'İptal Tarihi',
            name: 'Ad',
            description: 'Açıklama',
            monthly_price: 'Aylık Fiyat',
            yearly_price: 'Yıllık Fiyat',
            max_products: 'Ürün Limiti',
            max_channels: 'Kanal Limiti',
            max_users: 'Kullanıcı Limiti',
            max_orders_monthly: 'Aylık Sipariş Limiti',
            trial_days: 'Deneme Günü',
            is_active: 'Satışta',
            is_offered: 'Yeni Hesaplara Satışta',
            is_renewable: 'Yenilenebilir',
            is_default: 'Yeni Hesapların Başladığı Paket',
            days: 'Gün',
            reason: 'Gerekçe',
            price_vat_basis: 'Fiyatlarda KDV',
            sort: 'Sıra',
            features: 'Neler Dahil',
            highlight: 'Önerilen Paket',
            custom_pricing: 'Özel Fiyat',
            feature: 'Özellik',
            enabled: 'Açık',
            expires_at: 'Bitiş',
            note: 'Not',
            title: 'Başlık',
            level: 'Düzey',
            audience: 'Alıcı Sayısı',
            reference: 'Açıklama',
            period: 'Dönem',
            amount: 'Tutar',
            method: 'Ödeme Yöntemi',
            period_end: 'Dönem Bitişi',
            bank_holder: 'Hesap Sahibi',
            bank_name: 'Banka',
            iban: 'IBAN',
            provider: 'Hizmet',
            environment: 'Ortam',
            failure_reason: 'Neden',
            fields: 'Değişen Alanlar',
            count: 'Kayıt Sayısı',
            target: 'Taşındığı Depo',
            quantity: 'Adet',
            url: 'Adres',
            prefix: 'Anahtarın Başı',
            step: 'Hatalı Adım',
            failures: 'Art Arda Hatalı Deneme',
            locked: 'Hesap Kilitlendi',
            channel: 'Kanal',
            sku: 'Stok Kodu',
            prices: 'Fiyatlar',
            offers: 'Fiyat ve Stok',
            kind: 'Tür',
            format: 'Dosya Türü',
            existing: 'Katalogda Zaten Olan',
            over_limit: 'Paket Sınırını Aşan',
            failed: 'Eklenemeyen',
        },
        values: {
            yes: 'Evet',
            no: 'Hayır',
        },
        kinds: {
            einvoice: 'e-Fatura',
            earchive: 'e-Arşiv',
            credit_note: 'İade Faturası',
        },
        steps: {
            password: 'Parola',
            second_factor: 'Doğrulama Kodu',
        },
        vatBases: {
            inclusive: 'Dahil',
            exclusive: 'Eklenir',
        },
        statuses: {
            trialing: 'Deneme',
            trial: 'Deneme',
            active: 'Aktif',
            past_due: 'Ödeme Bekliyor',
            canceled: 'İptal',
            cancelled: 'İptal',
            expired: 'Süresi Doldu',
            suspended: 'Askıda',
            disabled: 'Kapalı',
            open: 'Açık',
            closed: 'Kapandı',
            approved: 'Onaylandı',
            rejected: 'Reddedildi',
            resolved: 'Sonuçlandı',
        },
    },

    system: {
        title: 'Sistem',
        subtitle: 'Makine dairesi: kuyruk, olay kutusu, webhook, ürün listesi. 30 saniyede bir yenilenir.',
        queue: 'İş Kuyruğu',
        available: 'Bekliyor',
        running: 'Çalışıyor',
        retryable: 'Yeniden Denenecek',
        scheduled: 'Zamanlanmış',
        discarded: 'Vazgeçilen (24s)',
        completed: 'Tamamlanan (24s)',
        outbox: 'Olay Kutusu',
        outboxPending: 'Yayınlanmamış',
        outboxStuck: '10 Dakikadan Eski',
        outboxFailing: 'Hatalı',
        delivery: 'Teslimatlar',
        webhooksPending: 'Bekleyen Webhook',
        webhooksFailed: 'Başarısız Webhook (24s)',
        feedsRunning: 'Okunan Ürün Listesi',
        feedsFailed: 'Okunamayan Ürün Listesi (24s)',
        access: 'Erişim',
        sessions: 'Açık Oturum',
        handoffs: 'Açık Destek Bağlantısı',
        rates: 'Kur Son Çekim',
        ratesStale: 'Kurlar bir günden eski.',
        byKind: 'İş Türüne Göre Kuyruk',
        byKindEmpty: 'Kuyruk Boş',
        kindColumns: { kind: 'Tür', state: 'Durum', count: 'Adet' },
        failures: 'Son Hatalar',
        failuresHint: 'Son ters giden işler ve ne dedikleri.',
        failuresEmpty: 'Yakın Zamanda Bir Hata Olmadı',
        failureColumns: {
            source: 'Nerede',
            reference: 'Ne',
            account: 'Hesap',
            error: 'Hata',
            when: 'Ne Zaman',
        },
        source: { job: 'İş', webhook: 'Webhook', feed: 'Ürün Listesi' },
        state: {
            available: 'Bekliyor',
            running: 'Çalışıyor',
            retryable: 'Yeniden Denenecek',
            scheduled: 'Zamanlanmış',
            pending: 'Beklemede',
            completed: 'Tamamlandı',
            discarded: 'Vazgeçildi',
            cancelled: 'İptal',
        },
    },

    requests: {
        title: 'Talepler',
        subtitle: 'İnsanların yazdıkları: siteden gelen mesajlar ve satıcıların paket değişikliği talepleri.',
        empty: 'Bekleyen Talep Yok',
        filterStatus: 'Durum',
        filterKind: 'Tür',
        status: { open: 'Açık', closed: 'Kapandı' },
        kind: { contact: 'Mesaj', plan_change: 'Paket Değişikliği' },
        source: { www: 'Site', panel: 'Panel' },
        columns: {
            when: 'Ne Zaman',
            kind: 'Tür',
            who: 'Kim',
            account: 'Hesap',
            plan: 'Paket',
            message: 'Mesaj',
            status: 'Durum',
        },
        open: 'Aç',
        detail: 'Talep',
        note: 'Not',
        noteHint: 'Ne yapıldı; bunu sonra okuyacak kişi için.',
        close: 'Talebi Kapat',
        reopen: 'Yeniden Aç',
        closedBy: (who: string, when: string) => `${who} kapattı · ${when}`,
        closed: 'Talep kapatıldı.',
        reply: 'E-posta ile Yanıtla',
    },

    plans: {
        title: 'Paketler',
        subtitle: 'Her paketin fiyatı, içeriği ve sınırları. Bir değişiklik o paketteki tüm hesaplara uygulanır.',
        columns: {
            plan: 'Paket',
            price: 'Aylık Fiyat',
            yearlyPrice: 'Yıllık Fiyat',
            limits: 'Sınırlar',
            trial: 'Deneme',
            subscribers: 'Hesap',
            status: 'Satış',
        },
        offered: 'Satışta',
        renewableOnly: 'Yalnızca Yenileme',
        withdrawn: 'Satılmıyor',
        isDefault: 'Varsayılan',
        limitParts: {
            products: (count: string) => `${count} ürün`,
            channels: (count: string) => `${count} pazaryeri hesabı`,
            users: (count: string) => `${count} kullanıcı`,
            orders: (count: string) => `Ayda ${count} sipariş`,
        },
        noLimits: 'Sınır yok',
        trialDays: (days: number) => `${days} gün`,
        edit: 'Düzenle',
        editTitle: (name: string) => `Paketi Düzenle: ${name}`,
        add: 'Paket Ekle',
        addTitle: 'Yeni Paket',
        contactSales: 'Teklifle belirlenir',
        free: 'Ücretsiz',
        notSoldYearly: 'Yıllık satılmıyor',
        highlighted: 'Önerilen Paket',
        plusVat: (price: string) => `${price} + KDV`,
        withVat: (total: string) => `KDV dahil ${total}`,
        perMonth: '/ ay',
        yearlyNote: (price: string) => `Yıllık peşin ${price} + KDV`,
        preview: 'Önizleme',
        previewHint: 'Kartın satıcılara ve sitede görüneceği hâli; fiyatlar platformun KDV esasına göre.',
        includes: 'Pakete dahil olanlar',
        notIncluded: 'Dahil değil',
        recommended: 'Önerilen Paket',
        fields: {
            key: 'Anahtar (Küçük Harf, ör. growth_plus)',
            name: 'Ad',
            description: 'Açıklama',
            price: 'Aylık Fiyat (₺)',
            yearlyPrice: 'Yıllık Fiyat (₺)',
            priceHint: (basis: string) => `Lira olarak. ${basis}`,
            yearlyPriceHint: 'Paket yalnızca aylık satılacaksa boş bırakın.',
            basisExclusive: 'Üzerine KDV eklenir.',
            basisInclusive: 'KDV dahildir.',
            currency: 'Para Birimi',
            maxProducts: 'Ürün Sınırı',
            maxChannels: 'Pazaryeri Hesabı Sınırı',
            maxUsers: 'Kullanıcı Sınırı',
            maxOrders: 'Aylık Sipariş Sınırı',
            trialDays: 'Deneme Günü',
            sort: 'Sıra',
            availability: 'Satış',
            isOffered: 'Yeni Hesaplara Satışta',
            isOfferedHint: 'Sitede ve başka paketteki hesaplara gösterilir.',
            isRenewable: 'Bu Paketteki Hesaplar Yenileyebilir',
            isRenewableHint: 'Kapalıyken bu paketteki hesap dönemi bitince paketten çıkar.',
            isDefault: 'Yeni Hesapların Denemesi Bu Pakette Başlar',
            isDefaultHint: 'Her zaman tek bir paket; değiştirmek için başka bir pakette işaretleyin.',
            unlimitedHint: 'Sınır koymamak için boş bırakın; boş sınır gösterilmez.',
            features: 'Ek Maddeler',
            featuresHint: 'Her satıra bir madde; pakete dahil olanlardan sonra gösterilir.',
            featureLevels: 'Özellik Düzeyleri',
            featureLevelsHint: 'Bu paketteki bir hesabın neleri kullanabileceği. Bir hesaba kendi sayfasından ayrıca düzey verilebilir.',
            defaultLevel: 'Tam Erişim (varsayılan)',
            highlight: 'Önerilen Paket Olarak Göster',
            customPricing: 'Fiyat Yok: Teklif ile Satılır',
        },
        saved: 'Paket güncellendi.',
        created: 'Paket eklendi.',
        readOnly: 'Paketi yalnızca süper yönetici değiştirebilir.',
    },

    payments: {
        title: 'Ödemeler',
        subtitle: 'Satıcıların paketleri için yaptığı ödemeler. Havale, onaylanana dek burada bekler.',
        filterStatus: 'Durum',
        empty: 'Ödeme Yok',
        emptyPending: 'Bekleyen Ödeme Yok',
        status: {
            pending: 'Onay Bekliyor',
            paid: 'Ödendi',
            held: 'Bekletiliyor',
            failed: 'Başarısız',
            cancelled: 'İptal Edildi',
            refunded: 'İade Edildi',
        },
        heldReason: 'Uygulanmadı: ödeme geldiğinde hesapta bekleyen bir paket değişikliği vardı.',
        periods: { monthly: 'Aylık', yearly: 'Yıllık' },
        methods: { bank_transfer: 'Havale/EFT', card: 'Kart ile Ödeme' },
        columns: {
            when: 'Tarih',
            account: 'Hesap',
            plan: 'Paket',
            amount: 'Tutar',
            method: 'Ödeme Yöntemi',
            reference: 'Açıklama',
            status: 'Durum',
            invoice: 'Fatura',
            agreement: 'Sözleşme',
        },
        agreement: {
            view: 'Görüntüle',
            viewLabel: (reference: string) => `${reference} ödemesinin sözleşmesini görüntüle`,
            title: 'Onaylanan Sözleşme',
            text: 'Ön Bilgilendirme Formu ve Mesafeli Hizmet Sözleşmesi',
            reference: 'Ödeme Numarası',
            acceptedBy: 'Onaylayan',
            acceptedAt: 'Onay Zamanı',
            ip: 'IP Adresi',
            userAgent: 'Tarayıcı',
            version: 'Sürüm',
            sha256: 'SHA-256',
            boxes: 'Onaylar',
            preInformation: 'Ön Bilgilendirme Formu’nu ve Mesafeli Hizmet Sözleşmesi’ni okudu, onayladı',
            commercialPurpose: 'Ticari veya mesleki faaliyeti için satın aldı',
            immediateStart: 'Hizmetin hemen başlamasını ve cayma hakkı olmadığını onayladı',
        },
        invoiceStatus: { pending: 'Kesiliyor', waiting: 'Bekliyor', issued: 'Kesildi', failed: 'Kesilemedi' },
        invoiceError: {
            unavailable: 'e-Fatura hizmetine ulaşılamadı.',
            company_incomplete: 'Firmanın fatura bilgileri eksik.',
            failed: 'Hizmet gerekçe belirtmeden reddetti.',
        },
        invoiceActions: 'Fatura İşlemleri',
        openInvoice: 'Faturayı Aç',
        issueInvoice: 'Faturayı Kes',
        fetchInvoice: 'Belgeyi Yeniden İste',
        recordInvoice: 'Başka Yerde Kesilen Faturayı Kaydet',
        issueOpen: 'Bekleyen Faturaları Kes',
        invoicingOff: 'Faturalar kesilmiyor. Önce bir e-Fatura hizmeti seçin ve düzenleyen bilgilerini tamamlayın.',
        invoiceSettings: 'Fatura Ayarları',
        issuedOpen: (count: number) => (count === 0 ? 'Bekleyen fatura yok.' : `${count} fatura kesilmek üzere sıraya alındı.`),
        confirmRecordInvoice: {
            title: 'Fatura Kaydedilecek',
            body: (reference: string) =>
                `${reference} için başka yerde kesilen faturanın numarasını kaydedin. Bu ödeme için burada ayrıca fatura kesilmez.`,
            action: 'Faturayı Kaydet',
        },
        invoiceNumber: 'Fatura Numarası',
        until: (date: string) => `${date} tarihine kadar`,
        startsOn: (date: string) => `${date} tarihinde başlar`,
        approve: 'Onayla',
        apply: 'Uygula',
        cancel: 'İptal Et',
        refund: 'İade Edildi Olarak İşaretle',
        refundCard: 'İade Et',
        cardPending: 'Tamamlanmadı',
        note: 'Not (İsteğe Bağlı)',
        confirmApprove: {
            title: 'Ödeme Onaylanacak',
            body: (account: string, plan: string, period: string, amount: string) =>
                `${account}, ${amount} karşılığında ${plan} paketini satın alır (${period}). Ödenmiş bir dönem sürüyorsa yeni paket o dönem bitince başlar. Yalnızca havale hesaba geçtiyse onaylayın.`,
            action: 'Ödemeyi Onayla',
        },
        confirmApply: {
            title: 'Ödeme Uygulanacak',
            body: (account: string, plan: string, period: string, amount: string) =>
                `${account} hesabının ${plan} paketi (${period}) için ödediği ${amount}, onaylanan bir ödeme gibi uygulanır: ödenmiş bir dönem sürüyorsa yeni paket o dönem bitince başlar. Ardından faturası kesilir.`,
            action: 'Ödemeyi Uygula',
        },
        confirmCancel: {
            title: 'Ödeme İptal Edilecek',
            body: (reference: string) => `${reference} kapanır ve satıcı mevcut paketinde kalır. Bundan sonra gelen bir havale iade edilir.`,
            action: 'Ödemeyi İptal Et',
        },
        confirmRefund: {
            title: 'Ödeme İade Edildi Olarak İşaretlenecek',
            body: (reference: string, amount: string) =>
                `${reference} için ${amount} tutarının geri gönderildiği kaydedilir. Paket olduğu gibi kalır; değişmesi gerekiyorsa hesabın sayfasından değiştirin.`,
            action: 'İade Edildi Olarak İşaretle',
        },
        confirmRefundCard: {
            title: 'Ödeme İade Edilecek',
            body: (reference: string, amount: string) =>
                `${reference} için ${amount}, ödemeyi alan hizmet üzerinden karta geri gönderilir. Paket olduğu gibi kalır; değişmesi gerekiyorsa hesabın sayfasından değiştirin.`,
            action: 'Karta İade Et',
        },
        done: {
            approved: 'Ödeme onaylandı. Paket hesaba işlendi.',
            applied: 'Ödeme uygulandı. Paket hesaba işlendi.',
            cancelled: 'Ödeme iptal edildi.',
            refunded: 'Ödeme iade edildi.',
            invoiceQueued: 'Fatura kesilmek üzere sıraya alındı.',
            invoiceRecorded: 'Fatura kaydedildi.',
        },
    },

    paymentSettings: {
        title: 'Ödeme Ayarları',
        subtitle: 'Satıcıların paketleri için nasıl ödeme yaptığı ve fiyatların KDV’yi içerip içermediği.',
        bankTransfer: 'Havale/EFT',
        bankTransferHint: 'Satıcıların havale gönderdiği hesap. Tutar ve açıklamaya yazacakları numarayla birlikte gösterilir.',
        notOffered: 'Hesap sahibi, banka ve IBAN girilene dek satıcılara havale sunulmaz.',
        readOnly: 'Ödemenin nereye yapılacağını yalnızca süper yönetici değiştirebilir.',
        holder: 'Hesap Sahibi',
        bank: 'Banka',
        iban: 'IBAN',
        ibanHint: 'Satıcılara dörderli gruplar hâlinde gösterilir. Üç alanı birden boşaltmak havaleyi kapatır.',
        saved: 'Ödeme ayarları kaydedildi.',
        vat: {
            title: 'Paket Fiyatlarında KDV',
            hint: 'Paketler’de yazılan fiyatın KDV’yi içerip içermediği. Değişiklikten sonra gösterilen her fiyat ve başlatılan her ödeme buna uyar; başlamış ödemeler tutarlarını korur.',
            readOnly: 'Yalnızca süper yönetici değiştirebilir.',
            exclusive: 'Fiyata KDV Eklenir',
            exclusiveHint: '₺1.499,00 yazılan paket ₺1.499,00 + KDV, KDV dahil ₺1.798,80 olarak satılır.',
            inclusive: 'Fiyat KDV’yi İçerir',
            inclusiveHint: '₺1.798,80 yazılan paket ₺1.499,00 + KDV, KDV dahil ₺1.798,80 olarak satılır.',
            save: 'Kaydet',
            saved: 'KDV esası kaydedildi.',
            confirm: {
                title: 'Fiyatların KDV Esası Değişecek',
                body: 'Gösterilen her paket fiyatı ve bundan sonra başlatılan her ödeme yeni esasa göre hesaplanır. Başlamış ödemeler tutarlarını korur.',
                action: 'KDV Esasını Değiştir',
            },
        },
        card: {
            title: 'Kart ile Ödeme',
            hint: 'Satıcıların kendi sayfasında kartla ödediği ödeme hizmeti. Bağlayın, kontrol edin, sonra kullanıma alın.',
            notOffered: 'Kontrolü geçmiş bir ödeme hizmeti kullanıma alınana dek satıcılara kartla ödeme sunulmaz.',
            empty: 'Bağlı ödeme hizmeti yok.',
            callbackUrl: (name: string) => `${name}: Bildirim Adresi`,
            copy: { copy: 'Kopyala', copied: 'Kopyalandı' },
            connect: 'Ödeme Hizmeti Bağla',
            verify: 'Bağlantıyı Kontrol Et',
            use: 'Kartla Ödemede Kullan',
            stop: 'Kartla Ödemeyi Kapat',
            withdraw: 'Bağlantıyı Kaldır',
            inUse: 'Kullanımda',
            status: { pending: 'Kontrol Edilmedi', connected: 'Bağlı', failed: 'Bağlantı Sorunu' },
            problem: { unavailable: 'Hizmete ulaşılamadı.', failed: 'Kontrol geçmedi.' },
            settingsHint: 'Hizmetin, satıcıların nasıl ödeyeceğine dair size bıraktığı seçimler.',
            back: 'Hizmetlere Dön',
            choose: 'Bağla',
            connected: 'Bağlı',
            notReady: 'Hazır Değil',
            confirmStop: {
                title: 'Kartla Ödeme Kapatılacak',
                body: 'Satıcılara artık kartla ödeme sunulmaz. Başlamış ödemeler hizmetin bildirimiyle yine kesinleşir.',
                action: 'Kartla Ödemeyi Kapat',
            },
            confirmWithdraw: {
                title: 'Bağlantı Kaldırılacak',
                body: 'Bu hizmetle yeni kart alınmaz. Anahtarları saklanır; aldığı ödemeler bu bağlantı üzerinden yine kesinleşir ve iade edilir.',
                action: 'Bağlantıyı Kaldır',
            },
            done: {
                connected: 'Ödeme hizmeti bağlandı. Kullanıma almadan önce kontrol edin.',
                chosen: 'Satıcılar artık bu hizmetle kartla ödüyor.',
                stopped: 'Kartla ödeme kapatıldı.',
                withdrawn: 'Bağlantı kaldırıldı.',
            },
        },
    },

    invoiceSettings: {
        title: 'Fatura Ayarları',
        subtitle: 'Ödenen her paket için kesilen fatura: hangi hizmetle ve kimin adına kesildiği.',
        notReady:
            'Faturalar henüz kesilmiyor. Ödenen paketlerin faturası, kontrolü geçmiş bir e-Fatura hizmeti seçilip düzenleyen bilgileri tamamlanana dek bekler; ardından Ödemeler’den kesilir.',
        issuer: {
            title: 'Düzenleyen Bilgileri',
            hint: 'Platformun her faturada ve satıcılarla kurulan her mesafeli hizmet sözleşmesinde yer alan yasal kimliği.',
            mersisNo: 'MERSİS No',
            mersisHint: 'On altı hane. Mesafeli hizmet sözleşmesinde yer alır; boşken sözleşmede gösterilmez.',
            kepAddress: 'KEP Adresi',
            kepHint: 'Mesafeli hizmet sözleşmesinde bildirim adresi olarak yer alır; boşken sözleşmede gösterilmez.',
            name: 'Ticari Unvan',
            taxNumber: 'Vergi Numarası',
            taxOffice: 'Vergi Dairesi',
            address: 'Adres',
            city: 'İl',
            district: 'İlçe',
            choose: 'Seçin',
            phone: 'Telefon',
            email: 'E-posta',
            incomplete: 'Unvan, vergi numarası, vergi dairesi ve adresin tamamı girilene dek fatura kesilmez.',
            readOnly: 'Düzenleyen bilgilerini yalnızca süper yönetici değiştirebilir.',
            saved: 'Düzenleyen bilgileri kaydedildi.',
        },
        service: {
            title: 'e-Fatura Hizmeti',
            hint: 'Faturaların kesildiği hizmet. Bağlayın, kontrol edin, sonra kullanıma alın. Hizmetin test ya da canlı tarafında kesilmesi tüm kurulum için ayarlanır.',
            notChosen: 'Kontrolü geçmiş bir e-Fatura hizmeti kullanıma alınana dek ödenen paketlerin faturası bekler.',
            empty: 'Bağlı e-Fatura hizmeti yok.',
            connect: 'e-Fatura Hizmeti Bağla',
            verify: 'Bağlantıyı Kontrol Et',
            use: 'Faturalarda Kullan',
            stop: 'Fatura Kesmeyi Durdur',
            withdraw: 'Bağlantıyı Kaldır',
            inUse: 'Kullanımda',
            status: { pending: 'Kontrol Edilmedi', connected: 'Bağlı', failed: 'Bağlantı Sorunu' },
            problem: { unavailable: 'Hizmete ulaşılamadı.', failed: 'Kontrol geçmedi.' },
            issues: { both: 'e-Fatura ve e-Arşiv', einvoice: 'Yalnızca e-Fatura', earchive: 'Yalnızca e-Arşiv' },
            settingsHint: 'Hizmetin faturalar için size bıraktığı seçimler.',
            back: 'Hizmetlere Dön',
            choose: 'Bağla',
            connected: 'Bağlı',
            notReady: 'Hazır Değil',
            confirmStop: {
                title: 'Fatura Kesme Durdurulacak',
                body: 'Ödenen paketlerin faturası, yeniden bir hizmet seçilene dek bekler. Kesilmiş faturalar olduğu gibi kalır.',
                action: 'Fatura Kesmeyi Durdur',
            },
            confirmWithdraw: {
                title: 'Bağlantı Kaldırılacak',
                body: 'Bu hizmetle yeni fatura kesilmez. Anahtarları saklanır; kestiği belgeler bu bağlantı üzerinden yine alınabilir.',
                action: 'Bağlantıyı Kaldır',
            },
            done: {
                connected: 'e-Fatura hizmeti bağlandı. Kullanıma almadan önce kontrol edin.',
                chosen: 'Faturalar artık bu hizmetle kesiliyor.',
                stopped: 'Fatura kesme durduruldu.',
                withdrawn: 'Bağlantı kaldırıldı.',
            },
        },
        offers: {
            title: 'Satıcılara Sunulan e-Fatura Hizmetleri',
            hint: 'Satıcıların fatura ayarlarından bağlayabileceği hizmetler. Bir hizmeti, bağlantısı canlı bir hesapla doğrulandıktan sonra açın. Gizlenen hizmetle yeni bağlantı kurulamaz; bağlı satıcılar bağlantılarını korur.',
            offered: 'Sunuluyor',
            hidden: 'Gizli',
            issues: {
                both: 'e-Fatura ve e-Arşiv',
                einvoice: 'Yalnızca e-Fatura',
                earchive: 'Yalnızca e-Arşiv',
                record: 'Satışı kaydeder; fatura hizmetin kendi ekranında kesilir',
            },
            sellers: (count: number) => (count === 0 ? 'Bağlı satıcı yok' : `${count} satıcı bağlı`),
            open: 'Satıcılara Aç',
            close: 'Satıcılardan Gizle',
            readOnly: 'Bir hizmeti satıcılara yalnızca süper yönetici açıp gizleyebilir.',
            confirmOpen: {
                title: (service: string) => `${service} Satıcılara Açılacak`,
                body: 'Tüm satıcılar bu hizmeti fatura ayarlarından bağlayabilir. Yalnızca bağlantısı canlı bir hesapla doğrulandıysa açın.',
                action: 'Satıcılara Aç',
            },
            confirmClose: {
                title: (service: string) => `${service} Satıcılardan Gizlenecek`,
                body: (sellers: number) =>
                    sellers === 0
                        ? 'Satıcılar bu hizmetle yeni bağlantı kuramaz.'
                        : `Satıcılar bu hizmetle yeni bağlantı kuramaz. Bağlı ${sellers} satıcı, bağlantısını ve bu hizmetle kesilen faturalarını korur.`,
                action: 'Satıcılardan Gizle',
            },
            done: { opened: 'Hizmet satıcılara açıldı.', hidden: 'Hizmet satıcılardan gizlendi.' },
        },
    },

    providerForm: {
        name: 'Bağlantı Adı',
        nameHint: 'Birden fazla bağlantı olduğunda ayırt etmek için kendi etiketiniz.',
        environment: 'Ortam',
        sandbox: 'Test',
        production: 'Canlı',
        testNote: (service: string) => `${service} test ortamına bağlanır; bu bağlantıyla gerçek bir ödeme alınmaz.`,
        useDefault: 'Varsayılan',
        submit: 'Kaydet ve Bağla',
    },
};

const LOCALES = { tr, en: en as Messages } as const;
export type LocaleKey = keyof typeof LOCALES;

const I18nContext = createContext<{
    locale: LocaleKey;
    setLocale: (locale: LocaleKey) => void;
    messages: Messages;
} | null>(null);

const STORAGE_KEY = 'locale';

function initialLocale(): LocaleKey {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored === 'tr' || stored === 'en') return stored;
    } catch {
        /* private browsing blocks storage */
    }
    return navigator.language.toLowerCase().startsWith('tr') ? 'tr' : 'en';
}

export function I18nProvider({ children }: { children: ReactNode }) {
    const [locale, setLocaleState] = useState<LocaleKey>(initialLocale);

    // The document speaks the chosen language: a screen reader's voice, and the
    // decimal separator DecimalInput shows, follow <html lang>.
    useEffect(() => {
        document.documentElement.lang = locale;
    }, [locale]);

    const value = useMemo(
        () => ({
            locale,
            setLocale: (next: LocaleKey) => {
                setLocaleState(next);
                try {
                    localStorage.setItem(STORAGE_KEY, next);
                } catch {
                    /* private browsing blocks storage */
                }
            },
            messages: LOCALES[locale],
        }),
        [locale],
    );

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
    const context = useContext(I18nContext);
    if (!context) throw new Error('useI18n must be used inside I18nProvider');
    return context;
}

export function useMessages(): Messages {
    return useI18n().messages;
}
